import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Target, Settings, LogOut, Search, RefreshCw, Plus, Eye, Trash2,
  Phone, Mail, MapPin, Clock, DollarSign, Users, UserCheck, Send,
  UserPlus, FileText, Save, Edit, X, Home, Printer, Calendar, BarChart3,
  AlertCircle, ClipboardList, Filter, Download, ExternalLink, Upload, FileSpreadsheet, Database,
  BookOpen, Video, CheckSquare, FileQuestion, ChevronRight, PlayCircle,
  TrendingUp, TrendingDown, PieChart, Activity, ArrowUpRight, ArrowDownRight, Archive, Ban, Brain, HardDrive,
  AlertTriangle, CheckCircle, Tag, Tags, SortAsc, SortDesc, MessageSquare, Briefcase, CreditCard, Award, XCircle
} from 'lucide-react';
import CompanyLogo from '../components/CompanyLogo';
import LanguageToggle from '../components/LanguageToggle';
import AIAssistant from '../components/AIAssistant';
import { useLanguage } from '../i18n/LanguageContext';
import { COMPANY } from '../config/company';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Client Card Component with Edit/Save/Delete functionality
const ClientCard = ({ client, language, onUpdate, allEstimates = [] }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: client.name || '',
    phone: client.phone || '',
    email: client.email || '',
    address: client.address || ''
  });
  const [saving, setSaving] = useState(false);

  // Get client's project type from their estimates or project_category
  const getClientProjectType = () => {
    // First check if client has project_category directly
    if (client.project_category) return client.project_category;
    
    // Then check their estimates
    const clientEstimates = allEstimates.filter(est => 
      (est.client_email && client.email && est.client_email.toLowerCase() === client.email.toLowerCase()) ||
      (est.client_name && client.name && est.client_name.toLowerCase() === client.name.toLowerCase())
    );
    if (clientEstimates.length > 0) {
      return clientEstimates[0].project_type || clientEstimates[0].project_category;
    }
    return null;
  };

  // Calculate if client has overdue invoices
  const hasOverdueInvoices = client.outstanding_balance > 0 || client.overdue_amount > 0;
  const isPaidInFull = client.total_paid > 0 && client.outstanding_balance === 0;
  
  // Check if new client (created in last 30 days)
  const isNewClient = () => {
    if (!client.created_at) return false;
    const createdDate = new Date(client.created_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return createdDate > thirtyDaysAgo;
  };

  // Project type badge colors
  const projectTypeBadges = {
    roofing: { bg: 'bg-red-100', text: 'text-red-700', label: 'Roofing' },
    kitchen: { bg: 'bg-green-100', text: 'text-green-700', label: 'Kitchen' },
    bathroom: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Bathroom' },
    flooring: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Flooring' },
    room_painting: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Painting' },
    interior_painting: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Painting' },
    pool_construction: { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'Pool' },
    hvac_replacement: { bg: 'bg-gray-200', text: 'text-gray-700', label: 'HVAC' },
    electrical_panel_upgrade: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Electrical' },
    drain_line_replacement: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Plumbing' },
    mold_remediation: { bg: 'bg-rose-100', text: 'text-rose-700', label: 'Mold' },
    water_mitigation: { bg: 'bg-sky-100', text: 'text-sky-700', label: 'Water' },
    drywall_repair: { bg: 'bg-stone-100', text: 'text-stone-700', label: 'Drywall' },
    fence_installation: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Fence' },
    new_construction: { bg: 'bg-yellow-200', text: 'text-yellow-800', label: 'New Build' },
    home_renovation: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Renovation' },
    home_addition: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Addition' },
    concrete_work: { bg: 'bg-slate-200', text: 'text-slate-700', label: 'Concrete' },
    stucco_repair: { bg: 'bg-neutral-200', text: 'text-neutral-700', label: 'Stucco' },
    window_replacement: { bg: 'bg-sky-100', text: 'text-sky-700', label: 'Windows' },
    tile_installation: { bg: 'bg-pink-100', text: 'text-pink-700', label: 'Tile' },
  };

  const projectType = getClientProjectType();
  const projectBadge = projectType ? projectTypeBadges[projectType] : null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${client.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });
      if (response.ok) {
        toast.success(language === 'es' ? 'Cliente actualizado' : 'Client updated');
        setIsEditing(false);
        onUpdate();
      } else {
        toast.error(language === 'es' ? 'Error al actualizar' : 'Failed to update');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al actualizar' : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este cliente?' : 'Delete this client?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${client.id}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(language === 'es' ? 'Cliente eliminado' : 'Client deleted');
        onUpdate();
      } else {
        toast.error(language === 'es' ? 'Error al eliminar' : 'Failed to delete');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  if (isEditing) {
    return (
      <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-300">
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500">{language === 'es' ? 'Nombre' : 'Name'}</label>
            <Input 
              value={editData.name} 
              onChange={(e) => setEditData({...editData, name: e.target.value})}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500">{language === 'es' ? 'Teléfono' : 'Phone'}</label>
              <Input 
                value={editData.phone} 
                onChange={(e) => setEditData({...editData, phone: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Email</label>
              <Input 
                type="email"
                value={editData.email} 
                onChange={(e) => setEditData({...editData, email: e.target.value})}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500">{language === 'es' ? 'Dirección' : 'Address'}</label>
            <Input 
              value={editData.address} 
              onChange={(e) => setEditData({...editData, address: e.target.value})}
              className="mt-1"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)} disabled={saving}>
              <X className="h-4 w-4 mr-1" />{language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} style={{ backgroundColor: COMPANY.primaryColor }} className="text-white">
              <Save className="h-4 w-4 mr-1" />{saving ? '...' : (language === 'es' ? 'Guardar' : 'Save')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border hover:border-amber-300 transition-colors">
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <Link to={`/clients/${client.id}`} className="font-semibold text-gray-900 hover:text-amber-600 hover:underline cursor-pointer">
            {client.name}
          </Link>
          
          {/* Status Badges */}
          {client.status === 'closed' && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-300 text-gray-700">Closed</span>
          )}
          {client.is_vip && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-200 text-amber-800">⭐ VIP</span>
          )}
          {isNewClient() && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">New</span>
          )}
          {client.is_repeat && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Repeat</span>
          )}
          {client.is_hot_lead && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">🔥 Hot</span>
          )}
          
          {/* Financial Badges */}
          {hasOverdueInvoices && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-200 text-red-800">Overdue</span>
          )}
          {isPaidInFull && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-200 text-green-800">Paid</span>
          )}
          {client.is_insurance && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700">Insurance</span>
          )}
          
          {/* Project Type Badge */}
          {projectBadge && (
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${projectBadge.bg} ${projectBadge.text}`}>
              {projectBadge.label}
            </span>
          )}
        </div>
        <div className="text-sm text-gray-500 flex flex-wrap gap-3 mt-1">
          {client.phone && <span className="flex items-center"><Phone className="h-3 w-3 mr-1" />{client.phone}</span>}
          {client.email && <span className="flex items-center"><Mail className="h-3 w-3 mr-1" />{client.email}</span>}
          {client.address && <span className="flex items-center"><MapPin className="h-3 w-3 mr-1" />{client.address}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Link to={`/clients/${client.id}`}>
          <Button size="sm" variant="ghost" className="text-gray-500 hover:text-gray-900" title={language === 'es' ? 'Ver' : 'View'}>
            <Eye className="h-4 w-4" />
          </Button>
        </Link>
        <Button size="sm" variant="ghost" className="text-blue-500 hover:text-blue-700" onClick={() => setIsEditing(true)} title={language === 'es' ? 'Editar' : 'Edit'}>
          <Edit className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={handleDelete} title={language === 'es' ? 'Eliminar' : 'Delete'}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get active tab from URL or default to 'pipeline'
  const searchParams = new URLSearchParams(location.search);
  const initialTab = searchParams.get('tab') || 'pipeline';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  const [user, setUser] = useState(null);
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [homeowners, setHomeowners] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [lumpSumLibrary, setLumpSumLibrary] = useState([]);
  const [showAddLumpSumModal, setShowAddLumpSumModal] = useState(false);
  const [newLumpSumItem, setNewLumpSumItem] = useState({ category: '', category_es: '', description: '', description_es: '' });
  const [lumpSumSearchQuery, setLumpSumSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('leads');
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, type: '', id: '', name: '' });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Selection state for import/export
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  
  // Advanced Client Filters & Sorting
  const [clientCityFilter, setClientCityFilter] = useState('');
  const [clientProjectTypeFilter, setClientProjectTypeFilter] = useState('');
  const [clientDateFromFilter, setClientDateFromFilter] = useState('');
  const [clientDateToFilter, setClientDateToFilter] = useState('');
  const [clientContractMinFilter, setClientContractMinFilter] = useState('');
  const [clientContractMaxFilter, setClientContractMaxFilter] = useState('');
  const [clientSortBy, setClientSortBy] = useState('name'); // name, date, contract_value
  const [clientSortOrder, setClientSortOrder] = useState('asc'); // asc, desc
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Report filters
  const [reportType, setReportType] = useState('leads');
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [reportStatusFilter, setReportStatusFilter] = useState('all');
  const [savingReportPdf, setSavingReportPdf] = useState(false);
  const reportRef = useRef(null);
  
  // Analytics state
  const [analyticsDateFrom, setAnalyticsDateFrom] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date.toISOString().split('T')[0];
  });
  const [analyticsDateTo, setAnalyticsDateTo] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1); // Add a month buffer for future dates
    return date.toISOString().split('T')[0];
  });
  const [savingAnalyticsPdf, setSavingAnalyticsPdf] = useState(false);
  const analyticsRef = useRef(null);
  
  // Storage Dashboard state
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [storageStats, setStorageStats] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [importDuplicates, setImportDuplicates] = useState([]);
  
  // Lead duplicate detection state
  const [showLeadDuplicateModal, setShowLeadDuplicateModal] = useState(false);
  const [leadDuplicates, setLeadDuplicates] = useState([]);
  const [pendingLeadData, setPendingLeadData] = useState(null);
  const [jobSuffix, setJobSuffix] = useState('');
  
  // Tags state
  const [predefinedTags, setPredefinedTags] = useState([]);
  const [selectedTagFilter, setSelectedTagFilter] = useState('');
  const [showTagModal, setShowTagModal] = useState(false);
  const [tagModalClient, setTagModalClient] = useState(null);
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  
  // Payment status state
  const [paymentStatus, setPaymentStatus] = useState({});
  const [selectedPaymentFilter, setSelectedPaymentFilter] = useState('');
  
  // Last contact state
  const [lastContactData, setLastContactData] = useState({});
  const [showFollowUpOnly, setShowFollowUpOnly] = useState(false);
  
  // Fetch predefined tags
  const fetchPredefinedTags = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/tags/predefined`);
      if (res.ok) {
        const data = await res.json();
        setPredefinedTags(data);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };
  
  // Fetch payment status for all clients
  const fetchPaymentStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/payment-status/all`);
      if (res.ok) {
        const data = await res.json();
        setPaymentStatus(data);
      }
    } catch (error) {
      console.error('Error fetching payment status:', error);
    }
  };
  
  // Fetch last contact for all clients
  const fetchLastContact = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/last-contact/all`);
      if (res.ok) {
        const data = await res.json();
        setLastContactData(data);
      }
    } catch (error) {
      console.error('Error fetching last contact:', error);
    }
  };
  
  // Fetch storage stats
  const fetchStorageStats = async () => {
    setStorageLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/storage/stats`);
      if (res.ok) {
        const data = await res.json();
        setStorageStats(data.gridfs);
      }
    } catch (error) {
      console.error('Error fetching storage stats:', error);
    } finally {
      setStorageLoading(false);
    }
  };
  
  // Save Report to PDF function
  const saveReportToPdf = async () => {
    if (!reportRef.current) return;
    
    setSavingReportPdf(true);
    toast.info(language === 'es' ? 'Generando PDF...' : 'Generating PDF...');
    
    try {
      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, (pdfHeight - 20) / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;
      
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      const fileName = `${reportType}_report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.success(language === 'es' ? 'PDF guardado' : 'PDF saved');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(language === 'es' ? 'Error al generar PDF' : 'Error generating PDF');
    } finally {
      setSavingReportPdf(false);
    }
  };
  
  // Save Analytics to PDF function
  const saveAnalyticsToPdf = async () => {
    if (!analyticsRef.current) return;
    
    setSavingAnalyticsPdf(true);
    toast.info(language === 'es' ? 'Generando PDF de analíticas...' : 'Generating analytics PDF...');
    
    try {
      const element = analyticsRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min((pdfWidth - 20) / imgWidth, (pdfHeight - 20) / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;
      
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      const dateRange = `${analyticsDateFrom}_to_${analyticsDateTo}`;
      const fileName = `analytics_report_${dateRange}.pdf`;
      pdf.save(fileName);
      
      toast.success(language === 'es' ? 'PDF de analíticas guardado' : 'Analytics PDF saved');
    } catch (error) {
      console.error('Error generating analytics PDF:', error);
      toast.error(language === 'es' ? 'Error al generar PDF' : 'Error generating PDF');
    } finally {
      setSavingAnalyticsPdf(false);
    }
  };
  
  const [newLead, setNewLead] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    project_type: 'new_construction',
    lead_status: 'estimate_provided'
  });

  useEffect(() => {
    // Check auth
    const token = localStorage.getItem('adminToken');
    const userData = localStorage.getItem('adminUser');
    if (!token || !userData) {
      toast.info(language === 'es' ? 'Por favor inicie sesión' : 'Please log in to continue');
      // Use window.location for more reliable redirect
      window.location.href = '/login';
      return;
    }
    setUser(JSON.parse(userData));
    fetchData();
    fetchPredefinedTags();
    fetchPaymentStatus();
    fetchLastContact();
  }, [language]);

  useEffect(() => {
    // Update URL when tab changes
    navigate(`/admin?tab=${activeTab}`, { replace: true });
  }, [activeTab, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadsRes, clientsRes, homeownersRes, templatesRes, lumpSumRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/estimates`),
        fetch(`${BACKEND_URL}/api/clients`),
        fetch(`${BACKEND_URL}/api/homeowners`),
        fetch(`${BACKEND_URL}/api/estimate-templates`),
        fetch(`${BACKEND_URL}/api/non-itemized-templates`)
      ]);
      if (leadsRes.ok) setLeads(await leadsRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (homeownersRes.ok) setHomeowners(await homeownersRes.json());
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (lumpSumRes.ok) setLumpSumLibrary(await lumpSumRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Lump Sum Library functions
  const handleAddLumpSumItem = async () => {
    if (!newLumpSumItem.category?.trim() || !newLumpSumItem.description?.trim()) {
      toast.error(language === 'es' ? 'Categoría y descripción son requeridos' : 'Category and description are required');
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/non-itemized-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLumpSumItem)
      });
      if (response.ok) {
        toast.success(language === 'es' ? 'Item agregado' : 'Item added');
        setNewLumpSumItem({ category: '', category_es: '', description: '', description_es: '' });
        setShowAddLumpSumModal(false);
        fetchData();
      } else {
        toast.error(language === 'es' ? 'Error al agregar' : 'Failed to add');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al agregar' : 'Failed to add');
    }
  };

  const handleDeleteLumpSumItem = async (itemId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/non-itemized-templates/${itemId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(language === 'es' ? 'Item eliminado' : 'Item deleted');
        fetchData();
      } else {
        toast.error(language === 'es' ? 'Error al eliminar' : 'Failed to delete');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  const seedLumpSumLibrary = async () => {
    const defaultItems = [
      // Demolition Labor
      { category: 'Demolition Labor', category_es: 'Demolición', description: 'Tear out wet drywall', description_es: 'Remover drywall mojado' },
      { category: 'Demolition Labor', category_es: 'Demolición', description: 'Remove damaged insulation', description_es: 'Remover aislamiento dañado' },
      { category: 'Demolition Labor', category_es: 'Demolición', description: 'Remove tile roofing', description_es: 'Remover techo de teja' },
      { category: 'Demolition Labor', category_es: 'Demolición', description: 'Remove shingle roofing', description_es: 'Remover techo de tejas asfálticas' },
      { category: 'Demolition Labor', category_es: 'Demolición', description: 'Remove underlayment', description_es: 'Remover membrana' },
      { category: 'Demolition Labor', category_es: 'Demolición', description: 'Remove gutters and downspouts', description_es: 'Remover canaletas y bajantes' },
      { category: 'Demolition Labor', category_es: 'Demolición', description: 'Remove fascia boards', description_es: 'Remover tablas de fascia' },
      { category: 'Demolition Labor', category_es: 'Demolición', description: 'Remove soffit panels', description_es: 'Remover paneles de sofito' },
      // Roofing Labor
      { category: 'Roofing Labor', category_es: 'Trabajo de Techo', description: 'Install synthetic underlayment', description_es: 'Instalar membrana sintética' },
      { category: 'Roofing Labor', category_es: 'Trabajo de Techo', description: 'Install tile roofing', description_es: 'Instalar techo de teja' },
      { category: 'Roofing Labor', category_es: 'Trabajo de Techo', description: 'Install shingle roofing', description_es: 'Instalar tejas asfálticas' },
      { category: 'Roofing Labor', category_es: 'Trabajo de Techo', description: 'Install metal roofing', description_es: 'Instalar techo de metal' },
      { category: 'Roofing Labor', category_es: 'Trabajo de Techo', description: 'Install new flashing', description_es: 'Instalar tapajuntas nuevos' },
      { category: 'Roofing Labor', category_es: 'Trabajo de Techo', description: 'Install ridge caps', description_es: 'Instalar cumbreras' },
      { category: 'Roofing Labor', category_es: 'Trabajo de Techo', description: 'Install drip edge', description_es: 'Instalar gotero' },
      { category: 'Roofing Labor', category_es: 'Trabajo de Techo', description: 'Re-install existing tile', description_es: 'Reinstalar teja existente' },
      // Carpentry
      { category: 'Carpentry', category_es: 'Carpintería', description: 'Replace rotten fascia boards', description_es: 'Reemplazar tablas de fascia podridas' },
      { category: 'Carpentry', category_es: 'Carpintería', description: 'Replace damaged plywood decking', description_es: 'Reemplazar madera contrachapada dañada' },
      { category: 'Carpentry', category_es: 'Carpintería', description: 'Install new soffit panels', description_es: 'Instalar paneles de sofito nuevos' },
      { category: 'Carpentry', category_es: 'Carpintería', description: 'Repair roof trusses', description_es: 'Reparar cerchas del techo' },
      // Gutters
      { category: 'Gutters', category_es: 'Canaletas', description: 'Install new aluminum gutters', description_es: 'Instalar canaletas de aluminio nuevas' },
      { category: 'Gutters', category_es: 'Canaletas', description: 'Install new downspouts', description_es: 'Instalar bajantes nuevos' },
      { category: 'Gutters', category_es: 'Canaletas', description: 'Install gutter guards', description_es: 'Instalar protectores de canaleta' },
      // Permits & Inspections
      { category: 'Permit', category_es: 'Permiso', description: 'Pull city/county building permit', description_es: 'Obtener permiso de construcción' },
      { category: 'Permit', category_es: 'Permiso', description: 'Schedule required inspections', description_es: 'Programar inspecciones requeridas' },
      { category: 'Permit', category_es: 'Permiso', description: 'NOC (Notice of Commencement) filing', description_es: 'Presentar aviso de inicio de obra' },
      // Cleanup
      { category: 'Cleanup', category_es: 'Limpieza', description: 'Debris removal and disposal', description_es: 'Remoción y disposición de escombros' },
      { category: 'Cleanup', category_es: 'Limpieza', description: 'Magnetic nail sweep', description_es: 'Barrido magnético de clavos' },
      { category: 'Cleanup', category_es: 'Limpieza', description: 'Final site cleanup', description_es: 'Limpieza final del sitio' },
      // Drywall
      { category: 'Drywall', category_es: 'Drywall', description: 'Install new drywall', description_es: 'Instalar drywall nuevo' },
      { category: 'Drywall', category_es: 'Drywall', description: 'Tape and mud drywall seams', description_es: 'Encintado y masillado de juntas' },
      { category: 'Drywall', category_es: 'Drywall', description: 'Apply texture to walls/ceiling', description_es: 'Aplicar textura a paredes/techo' },
      // Painting
      { category: 'Painting', category_es: 'Pintura', description: 'Prime and paint interior walls', description_es: 'Aplicar primer y pintar paredes interiores' },
      { category: 'Painting', category_es: 'Pintura', description: 'Paint exterior fascia and trim', description_es: 'Pintar fascia y molduras exteriores' },
      // Miscellaneous
      { category: 'Miscellaneous', category_es: 'Misceláneo', description: 'Install skylights', description_es: 'Instalar claraboyas' },
      { category: 'Miscellaneous', category_es: 'Misceláneo', description: 'Install roof vents', description_es: 'Instalar ventilaciones de techo' },
      { category: 'Miscellaneous', category_es: 'Misceláneo', description: 'Seal all penetrations', description_es: 'Sellar todas las penetraciones' }
    ];
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/non-itemized-templates/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(defaultItems)
      });
      if (response.ok) {
        toast.success(language === 'es' ? `${defaultItems.length} items agregados` : `${defaultItems.length} items added`);
        fetchData();
      } else {
        toast.error(language === 'es' ? 'Error al agregar items' : 'Failed to add items');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al agregar items' : 'Failed to add items');
    }
  };

  // Filter lump sum library by search
  const filteredLumpSumLibrary = useMemo(() => {
    if (!lumpSumSearchQuery) return lumpSumLibrary;
    const query = lumpSumSearchQuery.toLowerCase();
    return lumpSumLibrary.filter(item =>
      item.category?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query) ||
      item.category_es?.toLowerCase().includes(query) ||
      item.description_es?.toLowerCase().includes(query)
    );
  }, [lumpSumLibrary, lumpSumSearchQuery]);

  // Group lump sum items by category
  const lumpSumByCategory = useMemo(() => {
    return filteredLumpSumLibrary.reduce((acc, item) => {
      const cat = item.category || 'Other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});
  }, [filteredLumpSumLibrary]);

  // Template management functions
  const handleDeleteTemplate = async (templateId) => {
    if (deleteConfirmText.toUpperCase() !== 'DELETE') {
      toast.error(language === 'es' ? 'Por favor escriba DELETE para confirmar' : 'Please type DELETE to confirm');
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimate-templates/${templateId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(language === 'es' ? 'Plantilla eliminada' : 'Template deleted');
        setDeleteConfirmModal({ show: false, type: '', id: '', name: '' });
        setDeleteConfirmText('');
        fetchData();
      } else {
        toast.error(language === 'es' ? 'Error al eliminar' : 'Failed to delete');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate?.name?.trim()) {
      toast.error(language === 'es' ? 'Nombre requerido' : 'Name required');
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimate-templates/${editingTemplate.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingTemplate)
      });
      if (response.ok) {
        toast.success(language === 'es' ? 'Plantilla actualizada' : 'Template updated');
        setShowTemplateModal(false);
        setEditingTemplate(null);
        fetchData();
      } else {
        toast.error(language === 'es' ? 'Error al actualizar' : 'Failed to update');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al actualizar' : 'Failed to update');
    }
  };

  const handleCreateLeadFromTemplate = async (template) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: language === 'es' ? 'Nuevo Cliente' : 'New Client',
          client_email: '',
          client_phone: '',
          client_address: '',
          project_type: template.project_type,
          general_fees: template.general_fees.map(fee => ({ ...fee, id: crypto.randomUUID() })),
          line_items: template.line_items.map(item => ({ ...item, id: crypto.randomUUID() })),
          terms_and_conditions: template.terms_and_conditions,
          lead_status: 'estimate_provided'
        })
      });
      if (response.ok) {
        const newLead = await response.json();
        toast.success(language === 'es' ? '¡Lead creado desde plantilla!' : 'Lead created from template!');
        navigate(`/leads/${newLead.id}`);
      } else {
        toast.error(language === 'es' ? 'Error al crear lead' : 'Failed to create lead');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al crear lead' : 'Failed to create lead');
    }
  };

  const handleDeleteHomeowner = async (homeownerId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este usuario del portal?' : 'Delete this portal user?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/homeowners/${homeownerId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(language === 'es' ? 'Usuario eliminado' : 'User deleted');
        fetchData();
      } else {
        toast.error(language === 'es' ? 'Error al eliminar' : 'Failed to delete');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    toast.success(language === 'es' ? 'Sesión cerrada' : 'Logged out');
    navigate('/login');
  };

  // Add a duplicate client manually (force add)
  const handleAddDuplicateClient = async (duplicate) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: duplicate.name,
          email: duplicate.email || '',
          phone: duplicate.phone || '',
          address: duplicate.address || '',
          city: duplicate.city || '',
          state: duplicate.state || '',
          zip_code: duplicate.zip_code || '',
          company: duplicate.company || '',
          source: 'Import (Manual)',
          status: 'client'
        })
      });
      
      if (response.ok) {
        toast.success(language === 'es' ? `${duplicate.name} añadido` : `${duplicate.name} added`);
        // Remove from duplicates list
        setImportDuplicates(prev => prev.filter(d => d.row !== duplicate.row));
        fetchData();
      } else {
        toast.error(language === 'es' ? 'Error al añadir' : 'Error adding');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error al añadir' : 'Error adding');
    }
  };

  // Tag management functions
  const handleAddTagToClient = async (clientId, tag) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${clientId}/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag: tag.toLowerCase() })
      });
      if (response.ok) {
        fetchData();
        toast.success(language === 'es' ? 'Etiqueta añadida' : 'Tag added');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error' : 'Error');
    }
  };

  const handleRemoveTagFromClient = async (clientId, tag) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${clientId}/tags/${tag}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        fetchData();
        toast.success(language === 'es' ? 'Etiqueta eliminada' : 'Tag removed');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error' : 'Error');
    }
  };

  const handleBulkAddTags = async (tags) => {
    if (selectedClients.length === 0) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/tags/bulk-add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_ids: selectedClients, tags })
      });
      if (response.ok) {
        fetchData();
        setShowBulkTagModal(false);
        toast.success(language === 'es' ? 'Etiquetas añadidas' : 'Tags added');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error' : 'Error');
    }
  };

  // Bulk status change for clients
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedClients.length === 0) return;
    try {
      toast.loading(language === 'es' ? 'Actualizando estado...' : 'Updating status...');
      const response = await fetch(`${BACKEND_URL}/api/clients/bulk-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_ids: selectedClients, status: newStatus })
      });
      toast.dismiss();
      if (response.ok) {
        const result = await response.json();
        fetchData();
        setShowBulkStatusModal(false);
        setSelectedClients([]);
        toast.success(
          language === 'es' 
            ? `${result.updated_count || selectedClients.length} clientes actualizados` 
            : `${result.updated_count || selectedClients.length} clients updated`
        );
      } else {
        toast.error(language === 'es' ? 'Error al actualizar' : 'Failed to update');
      }
    } catch (error) {
      toast.dismiss();
      toast.error(language === 'es' ? 'Error' : 'Error');
    }
  };

  // Bulk status change for leads
  const handleBulkLeadStatusChange = async (newStatus) => {
    if (selectedLeads.length === 0) return;
    try {
      toast.loading(language === 'es' ? 'Actualizando estado...' : 'Updating status...');
      const response = await fetch(`${BACKEND_URL}/api/estimates/bulk-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estimate_ids: selectedLeads, lead_status: newStatus })
      });
      toast.dismiss();
      if (response.ok) {
        const result = await response.json();
        fetchData();
        setSelectedLeads([]);
        toast.success(
          language === 'es' 
            ? `${result.updated_count || selectedLeads.length} leads actualizados` 
            : `${result.updated_count || selectedLeads.length} leads updated`
        );
      } else {
        toast.error(language === 'es' ? 'Error al actualizar' : 'Failed to update');
      }
    } catch (error) {
      toast.dismiss();
      toast.error(language === 'es' ? 'Error' : 'Error');
    }
  };

  const getTagColor = (tagId) => {
    const predefined = predefinedTags.find(t => t.id === tagId || t.label.toLowerCase() === tagId.toLowerCase());
    return predefined?.color || '#6B7280';
  };

  const getTagLabel = (tagId) => {
    const predefined = predefinedTags.find(t => t.id === tagId || t.label.toLowerCase() === tagId.toLowerCase());
    return predefined?.label || tagId;
  };

  // Payment status helpers
  const getPaymentStatusBadge = (clientId) => {
    const status = paymentStatus[clientId];
    if (!status) return null;
    
    const statusConfig = {
      paid: { label: language === 'es' ? 'Pagado' : 'Paid', color: 'bg-green-500', textColor: 'text-white' },
      partial: { label: language === 'es' ? 'Parcial' : 'Partial', color: 'bg-yellow-500', textColor: 'text-white' },
      unpaid: { label: language === 'es' ? 'Sin Pagar' : 'Unpaid', color: 'bg-red-500', textColor: 'text-white' },
      no_contract: { label: language === 'es' ? 'Sin Contrato' : 'No Contract', color: 'bg-gray-300', textColor: 'text-gray-600' }
    };
    
    return statusConfig[status.status] || null;
  };

  const formatCurrencyShort = (amount) => {
    if (!amount || amount === 0) return '$0';
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}k`;
    }
    return `$${amount.toFixed(0)}`;
  };

  // Last contact helpers
  const formatRelativeTime = (days) => {
    if (days === null || days === undefined) return language === 'es' ? 'Desconocido' : 'Unknown';
    if (days === 0) return language === 'es' ? 'Hoy' : 'Today';
    if (days === 1) return language === 'es' ? 'Ayer' : 'Yesterday';
    if (days < 7) return language === 'es' ? `Hace ${days} días` : `${days} days ago`;
    if (days < 30) {
      const weeks = Math.floor(days / 7);
      return language === 'es' ? `Hace ${weeks} sem.` : `${weeks}w ago`;
    }
    if (days < 365) {
      const months = Math.floor(days / 30);
      return language === 'es' ? `Hace ${months} mes${months > 1 ? 'es' : ''}` : `${months}mo ago`;
    }
    const years = Math.floor(days / 365);
    return language === 'es' ? `Hace ${years} año${years > 1 ? 's' : ''}` : `${years}y ago`;
  };

  const getContactTypeIcon = (type) => {
    const types = {
      note: '📝',
      payment: '💰',
      task: '✅',
      activity: '📋',
      created: '🆕',
      contact: '📞'
    };
    return types[type] || '📌';
  };

  // Check for duplicate leads/clients by name, email, or phone
  const checkForDuplicates = (leadData) => {
    const duplicates = [];
    const name = leadData.client_name?.toLowerCase().trim();
    const email = leadData.client_email?.toLowerCase().trim();
    const phone = leadData.client_phone?.replace(/\D/g, '');
    
    // Check existing leads
    leads.forEach(lead => {
      const matchName = lead.client_name?.toLowerCase().trim() === name;
      const matchEmail = email && lead.client_email?.toLowerCase().trim() === email;
      const matchPhone = phone && lead.client_phone?.replace(/\D/g, '') === phone;
      
      if (matchName || matchEmail || matchPhone) {
        duplicates.push({
          type: 'lead',
          id: lead.id,
          name: lead.client_name,
          email: lead.client_email,
          phone: lead.client_phone,
          status: lead.lead_status,
          project_type: lead.project_type,
          matchReason: matchName ? 'Name' : matchEmail ? 'Email' : 'Phone'
        });
      }
    });
    
    // Check existing clients
    clients.forEach(client => {
      const matchName = client.name?.toLowerCase().trim() === name;
      const matchEmail = email && client.email?.toLowerCase().trim() === email;
      const matchPhone = phone && client.phone?.replace(/\D/g, '') === phone;
      
      if (matchName || matchEmail || matchPhone) {
        duplicates.push({
          type: 'client',
          id: client.id,
          name: client.name,
          email: client.email,
          phone: client.phone,
          status: 'client',
          matchReason: matchName ? 'Name' : matchEmail ? 'Email' : 'Phone'
        });
      }
    });
    
    return duplicates;
  };

  // Force create lead as new job (bypassing duplicate check)
  const handleForceCreateLead = async (withSuffix = false) => {
    if (!pendingLeadData) return;
    
    let leadData = { ...pendingLeadData };
    
    // Add suffix if provided
    if (withSuffix && jobSuffix.trim()) {
      leadData.client_name = `${leadData.client_name} - ${jobSuffix.trim()}`;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...leadData,
          status: 'draft',
          general_fees: [],
          line_items: [],
          subtotal: 0,
          tax_rate: 7.0,
          tax_amount: 0,
          grand_total: 0,
          terms_and_conditions: '',
          estimate_date: new Date().toISOString().split('T')[0],
          is_additional_job: true // Mark as additional job for same client
        })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(language === 'es' ? '¡Nuevo trabajo creado!' : 'New job created!');
        setShowLeadDuplicateModal(false);
        setShowAddLeadModal(false);
        setPendingLeadData(null);
        setJobSuffix('');
        setLeadDuplicates([]);
        setNewLead({ client_name: '', client_email: '', client_phone: '', client_address: '', project_type: 'new_construction', lead_status: 'estimate_provided' });
        navigate(`/leads/${data.id}`);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al crear' : 'Error creating');
    }
  };

  const handleAddLead = async () => {
    if (!newLead.client_name.trim()) {
      toast.error(language === 'es' ? 'Nombre requerido' : 'Name required');
      return;
    }
    
    // Check for duplicates first
    const duplicates = checkForDuplicates(newLead);
    if (duplicates.length > 0) {
      setLeadDuplicates(duplicates);
      setPendingLeadData(newLead);
      setShowLeadDuplicateModal(true);
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newLead,
          status: 'draft',
          general_fees: [],
          line_items: [],
          subtotal: 0,
          tax_rate: 7.0,
          tax_amount: 0,
          grand_total: 0,
          terms_and_conditions: '',
          estimate_date: new Date().toISOString().split('T')[0]
        })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(language === 'es' ? '¡Lead creado!' : 'Lead created!');
        setShowAddLeadModal(false);
        setNewLead({ client_name: '', client_email: '', client_phone: '', client_address: '', project_type: 'new_construction', lead_status: 'estimate_provided' });
        navigate(`/leads/${data.id}`);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al crear' : 'Error creating');
    }
  };

  const handleAddLeadFromTemplate = async () => {
    if (!newLead.client_name.trim()) {
      toast.error(language === 'es' ? 'Nombre requerido' : 'Name required');
      return;
    }
    
    // Check for duplicates first
    const duplicates = checkForDuplicates(newLead);
    if (duplicates.length > 0) {
      setLeadDuplicates(duplicates);
      setPendingLeadData({
        ...newLead,
        fromTemplate: true,
        template: selectedTemplate
      });
      setShowLeadDuplicateModal(true);
      return;
    }
    
    if (!selectedTemplate) {
      handleAddLead();
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: newLead.client_name,
          client_email: newLead.client_email,
          client_phone: newLead.client_phone,
          client_address: newLead.client_address,
          project_type: selectedTemplate.project_type || newLead.project_type,
          general_fees: selectedTemplate.general_fees?.map(fee => ({ ...fee, id: crypto.randomUUID() })) || [],
          line_items: selectedTemplate.line_items?.map(item => ({ ...item, id: crypto.randomUUID() })) || [],
          terms_and_conditions: selectedTemplate.terms_and_conditions || '',
          lead_status: 'estimate_provided',
          estimate_date: new Date().toISOString().split('T')[0]
        })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(language === 'es' ? '¡Lead creado desde plantilla!' : 'Lead created from template!');
        setShowAddLeadModal(false);
        setSelectedTemplate(null);
        setNewLead({ client_name: '', client_email: '', client_phone: '', client_address: '', project_type: 'new_construction', lead_status: 'estimate_provided' });
        navigate(`/leads/${data.id}`);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al crear' : 'Error creating');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (deleteConfirmText.toUpperCase() !== 'DELETE') {
      toast.error(language === 'es' ? 'Por favor escriba DELETE para confirmar' : 'Please type DELETE to confirm');
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates/${leadId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(language === 'es' ? 'Lead eliminado exitosamente' : 'Lead deleted successfully');
        setDeleteConfirmModal({ show: false, type: '', id: '', name: '' });
        setDeleteConfirmText('');
        fetchData();
      } else {
        toast.error(language === 'es' ? 'Error al eliminar' : 'Failed to delete');
      }
    } catch (error) { 
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este cliente?' : 'Delete this client?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${clientId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(language === 'es' ? 'Cliente eliminado' : 'Client deleted');
        fetchData();
      } else {
        toast.error(language === 'es' ? 'Error al eliminar' : 'Failed to delete');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  const openDeleteConfirm = (type, id, name) => {
    setDeleteConfirmModal({ show: true, type, id, name });
    setDeleteConfirmText('');
  };

  const handleConvertToClient = async (lead) => {
    // Check if already converted
    if (lead.converted_to_client) {
      toast.error(language === 'es' ? 'Este lead ya fue convertido a cliente' : 'This lead has already been converted to client');
      return;
    }
    
    // Check if a client with this lead ID already exists
    const existingClient = clients.find(c => c.converted_from_lead_id === lead.id);
    if (existingClient) {
      toast.error(language === 'es' ? 'Ya existe un cliente para este lead' : 'A client already exists for this lead');
      return;
    }
    
    if (!window.confirm(language === 'es' 
      ? `¿Convertir "${lead.client_name}" a cliente? El lead será movido al CRM.` 
      : `Convert "${lead.client_name}" to client? The lead will be moved to CRM.`)) {
      return;
    }
    try {
      // Create client from lead with ALL data
      const response = await fetch(`${BACKEND_URL}/api/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: lead.client_name,
          email: lead.client_email,
          phone: lead.client_phone,
          address: lead.client_address,
          source: 'Lead Conversion',
          status: 'client',
          // Transfer additional data - use project_category for client model
          project_category: lead.project_category || lead.project_type || '',
          notes: lead.notes || [],
          documents: lead.documents || [],
          photos: lead.photos || [],
          // Store reference to original estimate
          converted_from_lead_id: lead.id,
          converted_at: new Date().toISOString(),
          // Financial data
          total_estimate_value: lead.grand_total || 0
        })
      });
      if (response.ok) {
        const newClient = await response.json();
        
        // Transfer contacts from lead to client
        try {
          const contactsResponse = await fetch(`${BACKEND_URL}/api/contacts/lead/${lead.id}`);
          if (contactsResponse.ok) {
            const leadContacts = await contactsResponse.json();
            // Update each contact to link to the new client
            for (const contact of leadContacts) {
              await fetch(`${BACKEND_URL}/api/contacts/${contact.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  ...contact,
                  client_id: newClient.id,
                  lead_id: null  // Clear lead_id since it's now a client contact
                })
              });
            }
          }
        } catch (contactError) {
          console.error('Error transferring contacts:', contactError);
        }
        
        // Update lead status to won and mark as converted
        await fetch(`${BACKEND_URL}/api/estimates/${lead.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            ...lead, 
            lead_status: 'lead_won',
            converted_to_client: true,
            converted_at: new Date().toISOString(),
            client_id: newClient.id
          })
        });
        toast.success(language === 'es' ? '¡Lead convertido a cliente exitosamente!' : 'Lead successfully converted to client!');
        fetchData();
        // Switch to Clients sub-tab within Pipeline
        setStatusFilter('clients');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al convertir' : 'Error converting');
    }
  };

  const getLeadStatusBadge = (status) => {
    const config = {
      estimate_provided: { color: 'bg-blue-100 text-blue-700', label: language === 'es' ? 'Propuesta' : 'Proposal' },
      follow_up: { color: 'bg-yellow-100 text-yellow-700', label: language === 'es' ? 'Seguimiento' : 'Follow Up' },
      presentation_made: { color: 'bg-purple-100 text-purple-700', label: language === 'es' ? 'Presentado' : 'Presented' },
      lead_won: { color: 'bg-green-100 text-green-700', label: language === 'es' ? 'Ganado' : 'Won' },
      lead_lost: { color: 'bg-red-100 text-red-700', label: language === 'es' ? 'Perdido' : 'Lost' }
    };
    const c = config[status] || config.estimate_provided;
    return <Badge variant="outline" className={c.color}>{c.label}</Badge>;
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString() : 'N/A';

  // Email proposal to client using Resend with PDF attachment
  const handleEmailProposal = async (lead) => {
    if (!lead.client_email) {
      toast.error(language === 'es' ? 'No hay email del cliente' : 'No client email available');
      return;
    }
    
    const clientEmail = lead.client_email.trim();
    
    // Ask user what type of document to send
    const docType = window.confirm(
      language === 'es' 
        ? '¿Enviar como Factura? (Cancelar para enviar como Propuesta)'
        : 'Send as Invoice? (Cancel to send as Proposal)'
    ) ? 'invoice' : 'estimate';
    
    try {
      toast.loading(language === 'es' ? 'Generando PDF y enviando...' : 'Generating PDF and sending...');
      
      // We'll send without PDF for now from the dashboard
      // The full PDF can be sent from the Lead Detail page
      const response = await fetch(`${BACKEND_URL}/api/email/send-proposal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: lead.id,
          recipient_email: clientEmail,
          document_type: docType
        })
      });
      
      const result = await response.json();
      toast.dismiss();
      
      if (result.success) {
        toast.success(language === 'es' 
          ? `¡${docType === 'invoice' ? 'Factura' : 'Propuesta'} enviado a ${clientEmail}!` 
          : `${docType === 'invoice' ? 'Invoice' : 'Proposal'} sent to ${clientEmail}!`);
        fetchData();
      } else {
        toast.error(result.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.dismiss();
      console.error('Email error:', error);
      toast.error(language === 'es' ? 'Error al enviar email' : 'Error sending email');
    }
  };

  // Filter leads by category
  const coldLeads = leads.filter(lead => lead.lead_status === 'cold_lead' && !lead.converted_to_client);
  const doNotContactLeads = leads.filter(lead => lead.lead_status === 'do_not_contact' && !lead.converted_to_client);
  
  // UPDATED CATEGORIES (per user request):
  // Leads = ALL active leads (new_lead, follow_up, estimate_provided, presentation_made)
  //         Leads stay here even after a proposal is created
  // Proposals = Filtered view showing ONLY leads with estimate_provided status
  //             This is a subset/view, not exclusive - same leads appear in both tabs
  // Projects = lead_won (active jobs in progress)
  const newLeads = leads.filter(lead => 
    !lead.converted_to_client && 
    (lead.lead_status === 'new_lead' || lead.lead_status === 'follow_up' || 
     lead.lead_status === 'estimate_provided' || lead.lead_status === 'presentation_made') &&
    lead.lead_status !== 'cold_lead' && 
    lead.lead_status !== 'do_not_contact'
  );
  
  // Proposals tab - shows ALL proposals (pending + accepted/converted)
  // This tracks total proposals sent for reporting purposes
  const estimateLeads = leads.filter(lead => 
    lead.lead_status === 'estimate_provided' || 
    lead.lead_status === 'presentation_made' ||
    (lead.converted_to_client && lead.lead_status === 'lead_won')
  );
  
  const projectLeads = leads.filter(lead => 
    !lead.converted_to_client && 
    lead.lead_status === 'lead_won'
  );
  
  const lostLeads = leads.filter(lead => 
    !lead.converted_to_client && 
    lead.lead_status === 'lead_lost'
  );
  
  // For backward compatibility
  const activeLeads = leads.filter(lead => !lead.converted_to_client && lead.lead_status !== 'cold_lead' && lead.lead_status !== 'do_not_contact');
  const filteredLeads = activeLeads.filter(lead => {
    const matchesSearch = lead.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_phone?.includes(searchQuery);
    return matchesSearch;
  });
  
  const filteredNewLeads = newLeads.filter(lead => {
    const matchesSearch = lead.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_phone?.includes(searchQuery);
    return matchesSearch;
  });
  
  const filteredEstimateLeads = estimateLeads.filter(lead => {
    const matchesSearch = lead.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_phone?.includes(searchQuery);
    return matchesSearch;
  });
  
  const filteredProjectLeads = projectLeads.filter(lead => {
    const matchesSearch = lead.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_phone?.includes(searchQuery);
    return matchesSearch;
  });
  
  const filteredLostLeads = lostLeads.filter(lead => {
    const matchesSearch = lead.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_phone?.includes(searchQuery);
    return matchesSearch;
  });

  const filteredColdLeads = coldLeads.filter(lead => {
    const matchesSearch = lead.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_phone?.includes(searchQuery);
    return matchesSearch;
  });

  const filteredDoNotContactLeads = doNotContactLeads.filter(lead => {
    const matchesSearch = lead.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.client_phone?.includes(searchQuery);
    return matchesSearch;
  });

  // Combined archived leads (cold + do not contact)
  const archivedLeads = [...coldLeads, ...doNotContactLeads];
  const filteredArchivedLeads = [...filteredColdLeads, ...filteredDoNotContactLeads];

  // Extract unique cities from clients for filter dropdown
  const uniqueCities = [...new Set(clients.map(c => {
    // Try to extract city from address
    const addr = c.address || '';
    const parts = addr.split(',');
    if (parts.length >= 2) {
      return parts[parts.length - 2].trim(); // Usually city is second to last
    }
    return '';
  }).filter(Boolean))].sort();

  // Extract unique project types from clients
  const uniqueProjectTypes = [...new Set(clients.map(c => c.project_category || c.project_type || '').filter(Boolean))].sort();

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.phone?.includes(searchQuery);
    
    const matchesTag = !selectedTagFilter || 
      (client.tags && client.tags.some(tag => 
        tag.toLowerCase() === selectedTagFilter.toLowerCase() ||
        tag === selectedTagFilter
      ));
    
    const matchesPayment = !selectedPaymentFilter || 
      (paymentStatus[client.id] && paymentStatus[client.id].status === selectedPaymentFilter);
    
    const matchesFollowUp = !showFollowUpOnly || 
      (lastContactData[client.id] && lastContactData[client.id].needs_followup);

    // Advanced filters
    const matchesCity = !clientCityFilter || 
      (client.address && client.address.toLowerCase().includes(clientCityFilter.toLowerCase()));
    
    const matchesProjectType = !clientProjectTypeFilter || 
      (client.project_category || client.project_type || '').toLowerCase() === clientProjectTypeFilter.toLowerCase();
    
    const clientDate = client.created_at ? new Date(client.created_at) : null;
    const matchesDateFrom = !clientDateFromFilter || 
      (clientDate && clientDate >= new Date(clientDateFromFilter));
    const matchesDateTo = !clientDateToFilter || 
      (clientDate && clientDate <= new Date(clientDateToFilter + 'T23:59:59'));
    
    const contractValue = paymentStatus[client.id]?.contract_total || 0;
    const matchesContractMin = !clientContractMinFilter || 
      contractValue >= parseFloat(clientContractMinFilter);
    const matchesContractMax = !clientContractMaxFilter || 
      contractValue <= parseFloat(clientContractMaxFilter);
    
    return matchesSearch && matchesTag && matchesPayment && matchesFollowUp &&
           matchesCity && matchesProjectType && matchesDateFrom && matchesDateTo &&
           matchesContractMin && matchesContractMax;
  }).sort((a, b) => {
    // Sorting logic
    let comparison = 0;
    switch (clientSortBy) {
      case 'name':
        comparison = (a.name || '').localeCompare(b.name || '');
        break;
      case 'date':
        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
        comparison = dateA - dateB;
        break;
      case 'contract_value':
        const valueA = paymentStatus[a.id]?.contract_total || 0;
        const valueB = paymentStatus[b.id]?.contract_total || 0;
        comparison = valueA - valueB;
        break;
      default:
        comparison = 0;
    }
    return clientSortOrder === 'desc' ? -comparison : comparison;
  });

  // Calculate total revenue for filtered clients
  const totalFilteredRevenue = filteredClients.reduce((sum, client) => {
    return sum + (paymentStatus[client.id]?.contract_total || 0);
  }, 0);
  const totalFilteredPaid = filteredClients.reduce((sum, client) => {
    return sum + (paymentStatus[client.id]?.total_paid || 0);
  }, 0);
  const totalFilteredOutstanding = filteredClients.reduce((sum, client) => {
    return sum + (paymentStatus[client.id]?.outstanding || 0);
  }, 0);

  const stats = {
    totalLeads: newLeads.length,
    estimates: estimateLeads.length,
    projects: projectLeads.length,
    lost: lostLeads.length,
    archived: archivedLeads.length,
    totalValue: [...estimateLeads, ...projectLeads].reduce((sum, l) => sum + (l.grand_total || 0), 0),
    totalClients: clients.length
  };

  const leadStatusOptions = [
    { value: 'all', label: language === 'es' ? 'Todos' : 'All' },
    { value: 'estimate_provided', label: language === 'es' ? 'Propuesta Enviada' : 'Proposal Sent' },
    { value: 'follow_up', label: language === 'es' ? 'Seguimiento' : 'Follow Up' },
    { value: 'presentation_made', label: language === 'es' ? 'Presentado' : 'Presented' },
    { value: 'lead_won', label: language === 'es' ? 'Ganado' : 'Won' },
    { value: 'lead_lost', label: language === 'es' ? 'Perdido' : 'Lost' },
    { value: 'cold_lead', label: language === 'es' ? 'Frío' : 'Cold' },
    { value: 'do_not_contact', label: language === 'es' ? 'No Contactar' : 'Do Not Contact' }
  ];

  const projectTypes = [
    { value: 'kitchen', label: 'Kitchen Remodeling', label_es: 'Remodelación de Cocina' },
    { value: 'bathroom', label: 'Bathroom Remodeling', label_es: 'Remodelación de Baño' },
    { value: 'roofing', label: 'Roofing', label_es: 'Techos' },
    { value: 'pool_construction', label: 'Pool Construction', label_es: 'Construcción de Piscina' },
    { value: 'flooring', label: 'Flooring (Laminate)', label_es: 'Pisos (Laminado)' },
    { value: 'room_painting', label: 'Room Painting', label_es: 'Pintura de Habitación' },
    { value: 'interior_painting', label: 'Interior Painting', label_es: 'Pintura Interior' },
    { value: 'drain_line_replacement', label: 'Drain Line Replacement', label_es: 'Reemplazo de Drenaje' },
    { value: 'water_heater_replacement', label: 'Water Heater Replacement', label_es: 'Reemplazo de Calentador' },
    { value: 'water_mitigation', label: 'Water Mitigation', label_es: 'Mitigación de Agua' },
    { value: 'mold_remediation', label: 'Mold Remediation', label_es: 'Remediación de Moho' },
    { value: 'hvac_replacement', label: 'HVAC Replacement', label_es: 'Reemplazo de HVAC' },
    { value: 'window_replacement', label: 'Window Replacement', label_es: 'Reemplazo de Ventanas' },
    { value: 'electrical_panel_upgrade', label: 'Electrical Panel Upgrade', label_es: 'Actualización de Panel Eléctrico' },
    { value: 'drywall_repair', label: 'Drywall Repair', label_es: 'Reparación de Drywall' },
    { value: 'fence_installation', label: 'Fence Installation', label_es: 'Instalación de Cerca' },
    { value: 'concrete_work', label: 'Concrete (Driveway/Patio)', label_es: 'Trabajo de Concreto' },
    { value: 'stucco_repair', label: 'Stucco Repair', label_es: 'Reparación de Estuco' },
    { value: 'soffit_fascia', label: 'Soffit & Fascia', label_es: 'Sofito y Fascia' },
    { value: 'gutter_installation', label: 'Gutter Installation', label_es: 'Instalación de Canaletas' },
    { value: 'tile_installation', label: 'Tile Installation', label_es: 'Instalación de Azulejos' },
    { value: 'pressure_washing', label: 'Pressure Washing', label_es: 'Lavado a Presión' },
    { value: 'popcorn_ceiling_removal', label: 'Popcorn Ceiling Removal', label_es: 'Remoción de Techo Popcorn' },
    { value: 'home_renovation', label: 'Home Renovation', label_es: 'Renovación del Hogar' },
    { value: 'home_addition', label: 'Home Addition', label_es: 'Ampliación del Hogar' },
    { value: 'new_construction', label: 'New Construction', label_es: 'Nueva Construcción' }
  ];

  const tabs = [
    { id: 'pipeline', label: language === 'es' ? 'Pipeline' : 'Pipeline', icon: Target, showLabel: true },
    { id: 'analytics', label: language === 'es' ? 'Analíticas' : 'Analytics', icon: BarChart3, showLabel: true },
    { id: 'resources', label: language === 'es' ? 'Recursos' : 'Resources', icon: BookOpen, showLabel: true },
    { id: 'data', label: language === 'es' ? 'Datos' : 'Data', icon: Database, showLabel: false },
    { id: 'portal', label: language === 'es' ? 'Portal' : 'Portal', icon: Home, showLabel: false },
    { id: 'settings', label: language === 'es' ? 'Config' : 'Settings', icon: Settings, showLabel: false }
  ];

  // Don't render anything if not authenticated (prevents flash of content)
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-gray-500">{language === 'es' ? 'Verificando sesión...' : 'Verifying session...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <nav className="container mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <Link to="/" className="hover:opacity-80">
                <CompanyLogo className="h-10 w-auto" />
              </Link>
              {/* Tabs */}
              <div className="hidden sm:flex space-x-1 bg-gray-100 rounded-lg p-1">
                {tabs.map(tab => (
                  <Button
                    key={tab.id}
                    variant={activeTab === tab.id ? 'default' : 'ghost'}
                    onClick={() => {
                      if (tab.id === 'data') {
                        navigate('/data');
                      } else {
                        setActiveTab(tab.id);
                      }
                    }}
                    className={`${activeTab === tab.id ? 'text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'} ${!tab.showLabel ? 'px-2' : ''}`}
                    style={activeTab === tab.id ? { backgroundColor: COMPANY.primaryColor } : {}}
                    data-testid={`tab-${tab.id}`}
                    title={tab.label}
                  >
                    <tab.icon className={tab.showLabel ? "mr-2 h-4 w-4" : "h-4 w-4"} />
                    {tab.showLabel && tab.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <LanguageToggle />
              {/* Hidden Storage Dashboard Icon */}
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setShowStorageModal(true); fetchStorageStats(); }}
                className="text-gray-400 hover:text-gray-600 opacity-50 hover:opacity-100 transition-opacity"
                title="Storage Dashboard"
                data-testid="storage-dashboard-btn"
              >
                <HardDrive className="h-4 w-4" />
              </Button>
              <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-600">
                <span>{user?.email}</span>
              </div>
              <Button variant="ghost" onClick={handleLogout} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{language === 'es' ? 'Salir' : 'Logout'}</span>
              </Button>
            </div>
          </div>
          {/* Mobile Tabs */}
          <div className="flex sm:hidden space-x-1 mt-3 overflow-x-auto pb-1">
            {tabs.map(tab => (
              <Button
                key={tab.id}
                size="sm"
                variant={activeTab === tab.id ? 'default' : 'ghost'}
                onClick={() => {
                  if (tab.id === 'data') {
                    navigate('/data');
                  } else {
                    setActiveTab(tab.id);
                  }
                }}
                className={`whitespace-nowrap ${activeTab === tab.id ? 'text-white' : 'text-gray-600'}`}
                style={activeTab === tab.id ? { backgroundColor: COMPANY.primaryColor } : {}}
              >
                <tab.icon className="mr-1 h-4 w-4" />
                {tab.label}
              </Button>
            ))}
          </div>
        </nav>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-4">
        {/* PIPELINE TAB - Unified Leads & Clients View */}
        {activeTab === 'pipeline' && (
          <>
            {/* Compact Pipeline Stats - Single Row */}
            <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg border">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COMPANY.primaryColor }}></div>
                  <span className="text-sm font-medium">{newLeads.length}</span>
                  <span className="text-xs text-gray-500">{language === 'es' ? 'Leads' : 'Leads'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-sm font-medium">{clients.length}</span>
                  <span className="text-xs text-gray-500">{language === 'es' ? 'Clientes' : 'Clients'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  <span className="text-sm font-medium">{estimateLeads.length}</span>
                  <span className="text-xs text-gray-500">{language === 'es' ? 'Propuestas' : 'Proposals'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-sm font-medium">{projectLeads.length}</span>
                  <span className="text-xs text-gray-500">{language === 'es' ? 'Proyectos' : 'Projects'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-sm font-medium">{lostLeads.length}</span>
                  <span className="text-xs text-gray-500">{language === 'es' ? 'Perdidos' : 'Lost'}</span>
                </div>
              </div>
              <div className="ml-auto flex items-center gap-4">
                <div className="text-right">
                  <span className="text-xs text-gray-500">{language === 'es' ? 'Pipeline' : 'Pipeline'}</span>
                  <div className="text-sm font-bold" style={{ color: COMPANY.primaryColor }}>{formatCurrency(stats.totalValue)}</div>
                </div>
                <div className="text-right">
                  <span className="text-xs text-gray-500">{language === 'es' ? 'Conversión' : 'Win Rate'}</span>
                  <div className="text-sm font-bold text-green-600">{leads.length > 0 ? Math.round((projectLeads.length / leads.length) * 100) : 0}%</div>
                </div>
              </div>
            </div>

            {/* Sub-tabs: Leads | Clients | Estimates | Projects */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Button 
                variant={statusFilter === 'leads' ? 'default' : 'outline'} 
                onClick={() => setStatusFilter('leads')}
                style={statusFilter === 'leads' ? { backgroundColor: COMPANY.primaryColor } : {}}
                className={statusFilter === 'leads' ? 'text-white' : ''}
                size="sm"
              >
                <Target className="mr-1 h-3 w-3" />
                {language === 'es' ? 'Leads' : 'Leads'} ({newLeads.length})
              </Button>
              <Button 
                variant={statusFilter === 'clients' ? 'default' : 'outline'} 
                onClick={() => setStatusFilter('clients')}
                style={statusFilter === 'clients' ? { backgroundColor: '#2563eb' } : {}}
                className={statusFilter === 'clients' ? 'text-white' : ''}
                size="sm"
              >
                <Users className="mr-1 h-3 w-3" />
                {language === 'es' ? 'Clientes' : 'Clients'} ({clients.length})
              </Button>
              <Button 
                variant={statusFilter === 'estimates' ? 'default' : 'outline'} 
                onClick={() => setStatusFilter('estimates')}
                style={statusFilter === 'estimates' ? { backgroundColor: '#8b5cf6' } : {}}
                className={statusFilter === 'estimates' ? 'text-white' : ''}
                size="sm"
              >
                <FileText className="mr-1 h-3 w-3" />
                {language === 'es' ? 'Propuestas' : 'Proposals'} ({estimateLeads.length})
              </Button>
              <Button 
                variant={statusFilter === 'projects' ? 'default' : 'outline'} 
                onClick={() => setStatusFilter('projects')}
                style={statusFilter === 'projects' ? { backgroundColor: '#059669' } : {}}
                className={statusFilter === 'projects' ? 'text-white' : ''}
                size="sm"
              >
                <ClipboardList className="mr-1 h-3 w-3" />
                {language === 'es' ? 'Proyectos' : 'Projects'} ({projectLeads.length})
              </Button>
              <div className="border-l border-gray-300 mx-1"></div>
              <Button 
                variant={statusFilter === 'lost' ? 'default' : 'outline'} 
                onClick={() => setStatusFilter('lost')}
                style={statusFilter === 'lost' ? { backgroundColor: '#dc2626' } : {}}
                className={statusFilter === 'lost' ? 'text-white' : 'text-gray-500'}
                size="sm"
              >
                {language === 'es' ? 'Perdidos' : 'Lost'} ({lostLeads.length})
              </Button>
              <Button 
                variant={statusFilter === 'archived' ? 'default' : 'outline'} 
                onClick={() => setStatusFilter('archived')}
                style={statusFilter === 'archived' ? { backgroundColor: '#6b7280' } : {}}
                className={statusFilter === 'archived' ? 'text-white' : 'text-gray-500'}
                size="sm"
              >
                <Archive className="mr-1 h-3 w-3" />
                {language === 'es' ? 'Archivados' : 'Archived'} ({archivedLeads.length})
              </Button>
            </div>
            
            {/* Utility buttons */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Link to="/calendar">
                <Button 
                  variant="outline"
                  size="sm"
                >
                  <Calendar className="mr-1 h-3 w-3" />
                  {language === 'es' ? 'Calendario' : 'Calendar'}
                </Button>
              </Link>
              <Link to="/contacts">
                <Button 
                  variant="outline"
                  size="sm"
                >
                  <Users className="mr-1 h-3 w-3" />
                  {language === 'es' ? 'Contactos' : 'Contacts'}
                </Button>
              </Link>
              <Link to="/pricing">
                <Button 
                  variant="outline"
                  size="sm"
                  className="border-green-400 text-green-700 hover:bg-green-50"
                >
                  <DollarSign className="mr-1 h-3 w-3" />
                  {language === 'es' ? 'Lista de Precios' : 'Price List'}
                </Button>
              </Link>
            </div>

            {/* Search */}
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input placeholder={language === 'es' ? 'Buscar...' : 'Search...'} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 h-9" />
              </div>
              {/* Tag Filter */}
              <select
                value={selectedTagFilter}
                onChange={(e) => setSelectedTagFilter(e.target.value)}
                className="h-9 px-3 border rounded-md text-sm bg-white"
              >
                <option value="">{language === 'es' ? 'Todas las etiquetas' : 'All tags'}</option>
                {predefinedTags.map(tag => (
                  <option key={tag.id} value={tag.id}>{tag.label}</option>
                ))}
              </select>
              {/* Payment Status Filter */}
              <select
                value={selectedPaymentFilter}
                onChange={(e) => setSelectedPaymentFilter(e.target.value)}
                className="h-9 px-3 border rounded-md text-sm bg-white"
              >
                <option value="">{language === 'es' ? 'Estado de pago' : 'Payment status'}</option>
                <option value="paid">{language === 'es' ? '✅ Pagado' : '✅ Paid'}</option>
                <option value="partial">{language === 'es' ? '🟡 Parcial' : '🟡 Partial'}</option>
                <option value="unpaid">{language === 'es' ? '🔴 Sin Pagar' : '🔴 Unpaid'}</option>
                <option value="no_contract">{language === 'es' ? '⚪ Sin Contrato' : '⚪ No Contract'}</option>
              </select>
              {/* Follow-up Filter */}
              <Button
                variant={showFollowUpOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFollowUpOnly(!showFollowUpOnly)}
                className={showFollowUpOnly ? "bg-orange-500 hover:bg-orange-600 text-white" : "text-orange-600 border-orange-300 hover:bg-orange-50"}
              >
                <Clock className="h-3 w-3 mr-1" />
                {language === 'es' ? 'Seguimiento' : 'Follow-up'}
                {showFollowUpOnly && ` (${clients.filter(c => lastContactData[c.id]?.needs_followup).length})`}
              </Button>
              <Button variant="outline" size="sm" onClick={() => { fetchData(); fetchPaymentStatus(); fetchLastContact(); }}><RefreshCw className="h-3 w-3" /></Button>
              <Button size="sm" onClick={() => setShowAddLeadModal(true)} style={{ backgroundColor: COMPANY.primaryColor }} className="text-white">
                <Plus className="mr-1 h-3 w-3" />{language === 'es' ? 'Nuevo' : 'New'}
              </Button>
            </div>

            {/* LEADS LIST */}
            {statusFilter === 'leads' && (
              <Card>
                <CardHeader className="border-b pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center">
                      <Target className="mr-2 h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                      {language === 'es' ? 'Leads' : 'Leads'} ({filteredNewLeads.length})
                    </CardTitle>
                    <div className="flex gap-2">
                      <input
                        type="file"
                        id="import-leads-file"
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          try {
                            toast.loading(language === 'es' ? 'Importando...' : 'Importing...');
                            const response = await fetch(`${BACKEND_URL}/api/leads/import`, {
                              method: 'POST',
                              body: formData
                            });
                            const result = await response.json();
                            toast.dismiss();
                            
                            if (result.success) {
                              toast.success(result.message);
                              fetchData();
                            } else {
                              toast.error(result.detail || 'Import failed');
                            }
                          } catch (error) {
                            toast.dismiss();
                            toast.error(language === 'es' ? 'Error al importar' : 'Error importing');
                          }
                          e.target.value = '';
                        }}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`${BACKEND_URL}/api/leads/import/template`, '_blank')}
                        className="border-amber-400 text-amber-700 hover:bg-amber-50"
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-1" />
                        {language === 'es' ? 'Plantilla' : 'Template'}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => document.getElementById('import-leads-file').click()}
                        style={{ backgroundColor: COMPANY.primaryColor }}
                        className="text-white"
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        {language === 'es' ? 'Importar' : 'Import'}
                      </Button>
                      {selectedLeads.length > 0 && (
                        <Button
                          size="sm"
                          onClick={async () => {
                            const response = await fetch(`${BACKEND_URL}/api/leads/export/selected`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify(selectedLeads)
                            });
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'leads_selected_export.csv';
                            a.click();
                            window.URL.revokeObjectURL(url);
                          }}
                          style={{ backgroundColor: COMPANY.primaryColor }}
                          className="text-white"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          {language === 'es' ? `Exportar (${selectedLeads.length})` : `Export (${selectedLeads.length})`}
                        </Button>
                      )}
                      <Select
                        onValueChange={(format) => {
                          window.open(`${BACKEND_URL}/api/leads/export/${format}`, '_blank');
                        }}
                      >
                        <SelectTrigger className="w-[120px] h-9 border-amber-400 text-amber-700 hover:bg-amber-50">
                          <Download className="h-4 w-4 mr-1" />
                          {language === 'es' ? 'Exportar' : 'Export'}
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="csv">CSV</SelectItem>
                          <SelectItem value="excel">Excel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {loading ? (
                    <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div></div>
                  ) : filteredNewLeads.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">{language === 'es' ? 'No hay leads' : 'No leads found'}</div>
                  ) : (
                    <>
                      {/* Select All checkbox */}
                      <div className="flex items-center gap-2 mb-3 pb-3 border-b">
                        <Checkbox
                          checked={selectedLeads.length === filteredNewLeads.length && filteredNewLeads.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedLeads(filteredNewLeads.map(l => l.id));
                            } else {
                              setSelectedLeads([]);
                            }
                          }}
                        />
                        <span className="text-sm text-gray-600">
                          {language === 'es' ? 'Seleccionar todo' : 'Select all'} 
                          {selectedLeads.length > 0 && ` (${selectedLeads.length})`}
                        </span>
                      </div>
                      <div className="space-y-3">
                      {filteredNewLeads.map(lead => (
                        <div key={lead.id} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border hover:border-amber-300 transition-colors">
                          <Checkbox
                            checked={selectedLeads.includes(lead.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedLeads([...selectedLeads, lead.id]);
                              } else {
                                setSelectedLeads(selectedLeads.filter(id => id !== lead.id));
                              }
                            }}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <Link to={`/leads/${lead.id}`} className="font-semibold text-gray-900 hover:text-amber-600 hover:underline">
                                {lead.client_name}
                              </Link>
                              <Select 
                                value={lead.lead_status} 
                                onValueChange={async (value) => {
                                  try {
                                    await fetch(`${BACKEND_URL}/api/estimates/${lead.id}/lead-status`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ lead_status: value })
                                    });
                                    toast.success(language === 'es' ? 'Estado actualizado' : 'Status updated');
                                    fetchLeads();
                                  } catch (error) {
                                    console.error('Error:', error);
                                    toast.error(language === 'es' ? 'Error al actualizar' : 'Error updating');
                                  }
                                }}
                              >
                                <SelectTrigger className="w-[160px] h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new_lead">{language === 'es' ? 'Nuevo Prospecto' : 'New Lead'}</SelectItem>
                                  <SelectItem value="follow_up">{language === 'es' ? 'Seguimiento' : 'Follow Up'}</SelectItem>
                                  <SelectItem value="estimate_provided">{language === 'es' ? 'Propuesta Enviada' : 'Proposal Sent'}</SelectItem>
                                  <SelectItem value="presentation_made">{language === 'es' ? 'Presentación' : 'Presentation Made'}</SelectItem>
                                  <SelectItem value="lead_won">{language === 'es' ? 'Ganado' : 'Won'}</SelectItem>
                                  <SelectItem value="lead_lost">{language === 'es' ? 'Perdido' : 'Lost'}</SelectItem>
                                </SelectContent>
                              </Select>
                              {lead.converted_to_client && (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  <UserCheck className="h-3 w-3 mr-1" />
                                  {language === 'es' ? 'Cliente' : 'Client'}
                                </Badge>
                              )}
                              <span className="text-green-600 font-semibold">{formatCurrency(lead.grand_total)}</span>
                            </div>
                            <div className="text-sm text-gray-500 flex flex-wrap gap-3">
                              {lead.client_phone && <span className="flex items-center"><Phone className="h-3 w-3 mr-1" />{lead.client_phone}</span>}
                              {lead.client_email && <span className="flex items-center"><Mail className="h-3 w-3 mr-1" />{lead.client_email}</span>}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Link to={`/leads/${lead.id}`}>
                              <Button size="sm" variant="outline" title={language === 'es' ? 'Ver' : 'View'}><Eye className="h-4 w-4" /></Button>
                            </Link>
                            {lead.client_email && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => handleEmailProposal(lead)}
                                title={language === 'es' ? 'Enviar Propuesta' : 'Email Proposal'}
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                            {!lead.converted_to_client && (
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleConvertToClient(lead)}>
                                <UserCheck className="h-4 w-4 mr-1" />
                                <span className="hidden sm:inline">{language === 'es' ? 'A Cliente' : 'To Client'}</span>
                              </Button>
                            )}
                            {!lead.converted_to_client && lead.lead_status !== 'cold_lead' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-gray-600 hover:bg-gray-100"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  try {
                                    await fetch(`${BACKEND_URL}/api/estimates/${lead.id}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ lead_status: 'cold_lead' })
                                    });
                                    toast.success(language === 'es' ? 'Movido a Leads Fríos' : 'Moved to Cold Leads');
                                    fetchLeads();
                                  } catch (error) {
                                    console.error('Error:', error);
                                  }
                                }}
                                title={language === 'es' ? 'Seguimiento Después' : 'Follow Up Later'}
                              >
                                <Archive className="h-4 w-4" />
                              </Button>
                            )}
                            {!lead.converted_to_client && lead.lead_status !== 'do_not_contact' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-500 hover:bg-red-50"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  try {
                                    await fetch(`${BACKEND_URL}/api/estimates/${lead.id}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ lead_status: 'do_not_contact' })
                                    });
                                    toast.success(language === 'es' ? 'Marcado como No Contactar' : 'Marked as Do Not Contact');
                                    fetchLeads();
                                  } catch (error) {
                                    console.error('Error:', error);
                                  }
                                }}
                                title={language === 'es' ? 'No Contactar' : 'Do Not Contact'}
                              >
                                <Ban className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600" onClick={() => openDeleteConfirm('lead', lead.id, lead.client_name)} title={language === 'es' ? 'Eliminar' : 'Delete'}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* ESTIMATES LIST */}
            {statusFilter === 'estimates' && (
              <Card>
                <CardHeader className="border-b pb-3 bg-purple-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center text-purple-800">
                      <FileText className="mr-2 h-5 w-5 text-purple-600" />
                      {language === 'es' ? 'Propuestas Enviadas' : 'Proposals Sent'} ({filteredEstimateLeads.length})
                    </CardTitle>
                    <span className="text-sm font-semibold text-green-600">
                      {formatCurrency(filteredEstimateLeads.reduce((sum, l) => sum + (l.grand_total || 0), 0))}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {filteredEstimateLeads.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>{language === 'es' ? 'No hay propuestas pendientes' : 'No pending proposals'}</p>
                      <p className="text-sm">{language === 'es' ? 'Las propuestas enviadas aparecerán aquí' : 'Sent proposals will appear here'}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredEstimateLeads.map(lead => (
                        <div key={lead.id} className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200 hover:border-purple-400 transition-colors">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <Link to={`/leads/${lead.id}`} className="font-semibold text-gray-900 hover:text-purple-600 hover:underline">
                                {lead.client_name || 'Unnamed'}
                              </Link>
                              <Select 
                                value={lead.lead_status} 
                                onValueChange={async (value) => {
                                  try {
                                    await fetch(`${BACKEND_URL}/api/estimates/${lead.id}/lead-status`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ lead_status: value })
                                    });
                                    toast.success(language === 'es' ? 'Estado actualizado' : 'Status updated');
                                    fetchLeads();
                                  } catch (error) {
                                    console.error('Error:', error);
                                    toast.error(language === 'es' ? 'Error al actualizar' : 'Error updating');
                                  }
                                }}
                              >
                                <SelectTrigger className="w-[160px] h-7 text-xs bg-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new_lead">{language === 'es' ? 'Nuevo Prospecto' : 'New Lead'}</SelectItem>
                                  <SelectItem value="follow_up">{language === 'es' ? 'Seguimiento' : 'Follow Up'}</SelectItem>
                                  <SelectItem value="estimate_provided">{language === 'es' ? 'Propuesta Enviada' : 'Proposal Sent'}</SelectItem>
                                  <SelectItem value="presentation_made">{language === 'es' ? 'Presentación' : 'Presentation Made'}</SelectItem>
                                  <SelectItem value="lead_won">{language === 'es' ? 'Ganado' : 'Won'}</SelectItem>
                                  <SelectItem value="lead_lost">{language === 'es' ? 'Perdido' : 'Lost'}</SelectItem>
                                </SelectContent>
                              </Select>
                              <span className="text-green-600 font-semibold">{formatCurrency(lead.grand_total)}</span>
                            </div>
                            <div className="text-sm text-gray-500 flex flex-wrap gap-3">
                              {lead.client_phone && <span className="flex items-center"><Phone className="h-3 w-3 mr-1" />{lead.client_phone}</span>}
                              {lead.client_email && <span className="flex items-center"><Mail className="h-3 w-3 mr-1" />{lead.client_email}</span>}
                              <span className="text-purple-600">{lead.project_category || lead.project_type || 'No category'}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Link to={`/leads/${lead.id}`}>
                              <Button size="sm" variant="outline" title={language === 'es' ? 'Ver Propuesta' : 'View Proposal'}><Eye className="h-4 w-4" /></Button>
                            </Link>
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={async () => {
                                try {
                                  await fetch(`${BACKEND_URL}/api/estimates/${lead.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ lead_status: 'lead_won' })
                                  });
                                  toast.success(language === 'es' ? 'Marcado como ganado' : 'Marked as won');
                                  fetchLeads();
                                } catch (error) {
                                  console.error('Error:', error);
                                }
                              }}
                              title={language === 'es' ? 'Marcar Ganado' : 'Mark as Won'}
                            >
                              <UserCheck className="h-4 w-4 mr-1" />
                              {language === 'es' ? 'Ganado' : 'Won'}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={async () => {
                                try {
                                  await fetch(`${BACKEND_URL}/api/estimates/${lead.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ lead_status: 'lead_lost' })
                                  });
                                  toast.success(language === 'es' ? 'Marcado como perdido' : 'Marked as lost');
                                  fetchLeads();
                                } catch (error) {
                                  console.error('Error:', error);
                                }
                              }}
                              title={language === 'es' ? 'Marcar Perdido' : 'Mark as Lost'}
                            >
                              {language === 'es' ? 'Perdido' : 'Lost'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* PROJECTS LIST (Won leads - active jobs) */}
            {statusFilter === 'projects' && (
              <Card>
                <CardHeader className="border-b pb-3 bg-green-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center text-green-800">
                      <ClipboardList className="mr-2 h-5 w-5 text-green-600" />
                      {language === 'es' ? 'Proyectos Activos' : 'Active Projects'} ({filteredProjectLeads.length})
                    </CardTitle>
                    <span className="text-sm font-semibold text-green-600">
                      {formatCurrency(filteredProjectLeads.reduce((sum, l) => sum + (l.grand_total || 0), 0))}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {filteredProjectLeads.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <ClipboardList className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>{language === 'es' ? 'No hay proyectos activos' : 'No active projects'}</p>
                      <p className="text-sm">{language === 'es' ? 'Los proyectos ganados aparecerán aquí' : 'Won projects will appear here'}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredProjectLeads.map(lead => (
                        <div key={lead.id} className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200 hover:border-green-400 transition-colors">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <Link to={`/leads/${lead.id}`} className="font-semibold text-gray-900 hover:text-green-600 hover:underline">
                                {lead.client_name || 'Unnamed'}
                              </Link>
                              <Badge className="bg-green-200 text-green-800">
                                <ClipboardList className="h-3 w-3 mr-1" />
                                {language === 'es' ? 'En Progreso' : 'In Progress'}
                              </Badge>
                              <span className="text-green-600 font-semibold">{formatCurrency(lead.grand_total)}</span>
                            </div>
                            <div className="text-sm text-gray-500 flex flex-wrap gap-3">
                              {lead.client_phone && <span className="flex items-center"><Phone className="h-3 w-3 mr-1" />{lead.client_phone}</span>}
                              {lead.client_email && <span className="flex items-center"><Mail className="h-3 w-3 mr-1" />{lead.client_email}</span>}
                              <span className="text-green-600">{lead.project_category || lead.project_type || 'No category'}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Link to={`/leads/${lead.id}`}>
                              <Button size="sm" variant="outline" title={language === 'es' ? 'Ver Proyecto' : 'View Project'}><Eye className="h-4 w-4" /></Button>
                            </Link>
                            {!lead.converted_to_client && (
                              <Button 
                                size="sm" 
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={() => handleConvertToClient(lead)}
                                title={language === 'es' ? 'Convertir a Cliente' : 'Convert to Client'}
                              >
                                <UserCheck className="h-4 w-4 mr-1" />
                                {language === 'es' ? 'A Cliente' : 'To Client'}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* LOST LEADS LIST */}
            {statusFilter === 'lost' && (
              <Card>
                <CardHeader className="border-b pb-3 bg-red-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center text-red-800">
                      <Ban className="mr-2 h-5 w-5 text-red-600" />
                      {language === 'es' ? 'Leads Perdidos' : 'Lost Leads'} ({filteredLostLeads.length})
                    </CardTitle>
                    <span className="text-sm text-gray-500">
                      {formatCurrency(filteredLostLeads.reduce((sum, l) => sum + (l.grand_total || 0), 0))} {language === 'es' ? 'perdidos' : 'lost'}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {filteredLostLeads.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Ban className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>{language === 'es' ? 'No hay leads perdidos' : 'No lost leads'}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredLostLeads.map(lead => (
                        <div key={lead.id} className="flex items-center gap-3 p-4 bg-red-50 rounded-lg border border-red-200 hover:border-red-400 transition-colors">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <Link to={`/leads/${lead.id}`} className="font-semibold text-gray-700 hover:text-amber-600 hover:underline">
                                {lead.client_name || 'Unnamed'}
                              </Link>
                              <Badge className="bg-red-200 text-red-800">{language === 'es' ? 'Perdido' : 'Lost'}</Badge>
                              <span className="text-gray-500 line-through">{formatCurrency(lead.grand_total)}</span>
                            </div>
                            <div className="text-sm text-gray-500 flex flex-wrap gap-3">
                              {lead.client_phone && <span className="flex items-center"><Phone className="h-3 w-3 mr-1" />{lead.client_phone}</span>}
                              {lead.client_email && <span className="flex items-center"><Mail className="h-3 w-3 mr-1" />{lead.client_email}</span>}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Link to={`/leads/${lead.id}`}>
                              <Button size="sm" variant="outline"><Eye className="h-4 w-4" /></Button>
                            </Link>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-blue-600 hover:bg-blue-50"
                              onClick={async () => {
                                try {
                                  await fetch(`${BACKEND_URL}/api/estimates/${lead.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ lead_status: 'follow_up' })
                                  });
                                  toast.success(language === 'es' ? 'Reactivado' : 'Reactivated');
                                  fetchLeads();
                                } catch (error) {
                                  console.error('Error:', error);
                                }
                              }}
                            >
                              <RefreshCw className="h-4 w-4 mr-1" />
                              {language === 'es' ? 'Reactivar' : 'Reactivate'}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-600 hover:bg-red-100"
                              onClick={() => openDeleteConfirm('lead', lead.id, lead.client_name)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* ARCHIVED LEADS LIST (Cold + Do Not Contact) */}
            {statusFilter === 'archived' && (
              <Card>
                <CardHeader className="border-b pb-3 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center text-gray-700">
                      <Archive className="mr-2 h-5 w-5 text-gray-500" />
                      {language === 'es' ? 'Leads Archivados' : 'Archived Leads'} ({filteredArchivedLeads.length})
                    </CardTitle>
                    <p className="text-sm text-gray-500">
                      {language === 'es' ? 'Leads fríos y no contactar' : 'Cold leads and do not contact'}
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {filteredArchivedLeads.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Archive className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>{language === 'es' ? 'No hay leads archivados' : 'No archived leads'}</p>
                      <p className="text-sm">{language === 'es' ? 'Los leads archivados aparecerán aquí' : 'Archived leads will appear here'}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredArchivedLeads.map(lead => {
                        const isDoNotContact = lead.lead_status === 'do_not_contact';
                        return (
                          <div key={lead.id} className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${isDoNotContact ? 'bg-red-50 border-red-200 hover:border-red-400' : 'bg-gray-50 hover:border-gray-400'}`}>
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-1">
                                <Link to={`/leads/${lead.id}`} className="font-semibold text-gray-700 hover:text-amber-600 hover:underline">
                                  {lead.client_name || 'Unnamed'}
                                </Link>
                                {isDoNotContact ? (
                                  <Badge className="bg-red-200 text-red-800">
                                    <Ban className="h-3 w-3 mr-1" />
                                    {language === 'es' ? 'No Contactar' : 'DNC'}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-gray-200 text-gray-700">{language === 'es' ? 'Frío' : 'Cold'}</Badge>
                                )}
                                <span className="text-green-600 font-semibold">${(lead.grand_total || 0).toLocaleString()}</span>
                              </div>
                              <div className="text-sm text-gray-500 flex flex-wrap gap-3">
                                {lead.client_phone && <span className="flex items-center"><Phone className="h-3 w-3 mr-1" />{lead.client_phone}</span>}
                                {lead.client_email && <span className="flex items-center"><Mail className="h-3 w-3 mr-1" />{lead.client_email}</span>}
                                <span className="text-gray-400">{lead.project_category || lead.project_type || 'No category'}</span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Link to={`/leads/${lead.id}`}>
                                <Button size="sm" variant="outline" title={language === 'es' ? 'Ver' : 'View'}><Eye className="h-4 w-4" /></Button>
                              </Link>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-blue-600 hover:bg-blue-50"
                                onClick={async () => {
                                  try {
                                    await fetch(`${BACKEND_URL}/api/estimates/${lead.id}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ lead_status: 'follow_up' })
                                    });
                                    toast.success(language === 'es' ? 'Reactivado' : 'Reactivated');
                                    fetchLeads();
                                  } catch (error) {
                                    console.error('Error:', error);
                                  }
                                }}
                                title={language === 'es' ? 'Reactivar' : 'Reactivate'}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                              {!isDoNotContact && (
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleConvertToClient(lead)}>
                                  <UserCheck className="h-4 w-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-400 hover:text-red-600" 
                                onClick={() => openDeleteConfirm('lead', lead.id, lead.client_name)}
                                title={language === 'es' ? 'Eliminar' : 'Delete'}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* CLIENTS LIST */}
            {statusFilter === 'clients' && (
              <>
                {/* Financial Overview Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <Card className="p-3 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-amber-600" />
                      <div>
                        <div className="text-xs text-amber-700">{language === 'es' ? 'Total Contratos' : 'Total Contracts'}</div>
                        <div className="text-xl font-bold text-amber-600">{formatCurrencyShort(totalFilteredRevenue)}</div>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-green-600" />
                      <div>
                        <div className="text-xs text-green-700">{language === 'es' ? 'Pagado' : 'Paid'}</div>
                        <div className="text-xl font-bold text-green-600">{formatCurrencyShort(totalFilteredPaid)}</div>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-3 bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-red-600" />
                      <div>
                        <div className="text-xs text-red-700">{language === 'es' ? 'Pendiente' : 'Outstanding'}</div>
                        <div className="text-xl font-bold text-red-600">{formatCurrencyShort(totalFilteredOutstanding)}</div>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-600" />
                      <div>
                        <div className="text-xs text-blue-700">{language === 'es' ? 'Clientes' : 'Clients'}</div>
                        <div className="text-xl font-bold text-blue-600">{filteredClients.length}</div>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Project Stage Progress Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                  <Card className="p-3 text-center bg-orange-50 border-orange-200">
                    <div className="text-2xl font-bold text-orange-600">{clients.filter(c => !c.project_stage || c.project_stage === 'permit_approved').length}</div>
                    <div className="text-xs text-orange-700">{language === 'es' ? 'Permiso' : 'Permit'}</div>
                  </Card>
                  <Card className="p-3 text-center bg-yellow-50 border-yellow-200">
                    <div className="text-2xl font-bold text-yellow-600">{clients.filter(c => c.project_stage === 'first_inspection').length}</div>
                    <div className="text-xs text-yellow-700">{language === 'es' ? '1ra Insp.' : '1st Insp.'}</div>
                  </Card>
                  <Card className="p-3 text-center bg-lime-50 border-lime-200">
                    <div className="text-2xl font-bold text-lime-600">{clients.filter(c => c.project_stage === 'second_inspection').length}</div>
                    <div className="text-xs text-lime-700">{language === 'es' ? '2da Insp.' : '2nd Insp.'}</div>
                  </Card>
                  <Card className="p-3 text-center bg-green-50 border-green-200">
                    <div className="text-2xl font-bold text-green-600">{clients.filter(c => c.project_stage === 'final_inspection').length}</div>
                    <div className="text-xs text-green-700">{language === 'es' ? 'Final' : 'Final'}</div>
                  </Card>
                  <Card className="p-3 text-center bg-emerald-50 border-emerald-200">
                    <div className="text-2xl font-bold text-emerald-600">{clients.filter(c => c.project_stage === 'payment_received').length}</div>
                    <div className="text-xs text-emerald-700">{language === 'es' ? 'Pagado' : 'Paid'}</div>
                  </Card>
                </div>

                {/* Clients List Card */}
                <Card>
                  <CardHeader className="border-b pb-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <CardTitle className="flex items-center">
                        <Users className="mr-2 h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                        {language === 'es' ? 'Clientes' : 'Clients'} ({filteredClients.length})
                        {selectedTagFilter && <Badge className="ml-2" style={{ backgroundColor: getTagColor(selectedTagFilter) }}>{getTagLabel(selectedTagFilter)}</Badge>}
                      </CardTitle>
                      <div className="flex gap-2 flex-wrap">
                        {/* Advanced Filters Toggle */}
                        <Button
                          size="sm"
                          variant={showAdvancedFilters ? "default" : "outline"}
                          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                          className={showAdvancedFilters ? "bg-amber-600 text-white" : "border-amber-400 text-amber-700"}
                        >
                          <Filter className="h-4 w-4 mr-1" />
                          {language === 'es' ? 'Filtros' : 'Filters'}
                        </Button>
                        <input
                          type="file"
                          id="import-clients-file"
                          accept=".csv,.xlsx,.xls"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            try {
                              toast.loading(language === 'es' ? 'Importando...' : 'Importing...');
                              const response = await fetch(`${BACKEND_URL}/api/clients/import`, {
                                method: 'POST',
                                body: formData
                              });
                              const result = await response.json();
                              toast.dismiss();
                              
                              if (result.success) {
                                toast.success(result.message);
                                fetchData();
                                
                                // Show duplicates modal if there are duplicates
                                if (result.duplicates && result.duplicates.length > 0) {
                                  setImportDuplicates(result.duplicates);
                                  setShowDuplicatesModal(true);
                                }
                              } else {
                                toast.error(result.detail || 'Import failed');
                              }
                            } catch (error) {
                              toast.dismiss();
                              toast.error(language === 'es' ? 'Error al importar' : 'Error importing');
                            }
                            e.target.value = '';
                          }}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`${BACKEND_URL}/api/clients/import/template`, '_blank')}
                          className="border-amber-400 text-amber-700 hover:bg-amber-50"
                        >
                          <FileSpreadsheet className="h-4 w-4 mr-1" />
                          {language === 'es' ? 'Plantilla' : 'Template'}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => document.getElementById('import-clients-file').click()}
                          style={{ backgroundColor: COMPANY.primaryColor }}
                          className="text-white"
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          {language === 'es' ? 'Importar' : 'Import'}
                        </Button>
                        {selectedClients.length > 0 && (
                          <>
                            <Button
                              size="sm"
                              onClick={async () => {
                                const response = await fetch(`${BACKEND_URL}/api/clients/export/selected`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify(selectedClients)
                                });
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = 'clients_selected_export.csv';
                                a.click();
                                window.URL.revokeObjectURL(url);
                              }}
                              style={{ backgroundColor: COMPANY.primaryColor }}
                              className="text-white"
                            >
                              <Download className="h-4 w-4 mr-1" />
                              {language === 'es' ? `Exportar (${selectedClients.length})` : `Export (${selectedClients.length})`}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={async () => {
                                if (!window.confirm(
                                  language === 'es' 
                                    ? `¿Eliminar ${selectedClients.length} cliente(s) seleccionado(s)? Esta acción no se puede deshacer.`
                                    : `Delete ${selectedClients.length} selected client(s)? This cannot be undone.`
                                )) return;
                                
                                try {
                                  toast.loading(language === 'es' ? 'Eliminando...' : 'Deleting...');
                                  const response = await fetch(`${BACKEND_URL}/api/clients/delete-selected`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ client_ids: selectedClients })
                                  });
                                  const result = await response.json();
                                  toast.dismiss();
                                  
                                  if (result.success) {
                                    toast.success(
                                      language === 'es' 
                                        ? `${result.deleted_count} cliente(s) eliminado(s)`
                                        : `${result.deleted_count} client(s) deleted`
                                    );
                                    setSelectedClients([]);
                                    fetchData();
                                  } else {
                                    toast.error(result.detail || 'Delete failed');
                                  }
                                } catch (error) {
                                  toast.dismiss();
                                  toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
                                }
                              }}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              {language === 'es' ? `Eliminar (${selectedClients.length})` : `Delete (${selectedClients.length})`}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowBulkTagModal(true)}
                              className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            >
                              <Tags className="h-4 w-4 mr-1" />
                              {language === 'es' ? 'Etiquetar' : 'Tag'} ({selectedClients.length})
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowBulkStatusModal(true)}
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                              data-testid="bulk-status-change-btn"
                            >
                              <RefreshCw className="h-4 w-4 mr-1" />
                              {language === 'es' ? 'Cambiar Estado' : 'Change Status'} ({selectedClients.length})
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            if (!window.confirm(
                              language === 'es' 
                                ? `⚠️ ADVERTENCIA: ¿Eliminar TODOS los ${clients.length} clientes? Esta acción NO se puede deshacer.`
                                : `⚠️ WARNING: Delete ALL ${clients.length} clients? This CANNOT be undone.`
                            )) return;
                            
                            // Double confirm for safety
                            const confirmText = language === 'es' ? 'ELIMINAR TODO' : 'DELETE ALL';
                            const userInput = window.prompt(
                              language === 'es'
                                ? `Escribe "${confirmText}" para confirmar:`
                                : `Type "${confirmText}" to confirm:`
                            );
                            
                            if (userInput !== confirmText) {
                              toast.error(language === 'es' ? 'Confirmación incorrecta' : 'Incorrect confirmation');
                              return;
                            }
                            
                            try {
                              toast.loading(language === 'es' ? 'Eliminando todos los clientes...' : 'Deleting all clients...');
                              const response = await fetch(`${BACKEND_URL}/api/clients/delete-all`, {
                                method: 'DELETE'
                              });
                              const result = await response.json();
                              toast.dismiss();
                              
                              if (result.success) {
                                toast.success(
                                  language === 'es' 
                                    ? `${result.deleted_count} cliente(s) eliminado(s)`
                                    : `${result.deleted_count} client(s) deleted`
                                );
                                setSelectedClients([]);
                                fetchData();
                              } else {
                                toast.error(result.detail || 'Delete failed');
                              }
                            } catch (error) {
                              toast.dismiss();
                              toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
                            }
                          }}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          {language === 'es' ? 'Eliminar Todo' : 'Delete All'}
                        </Button>
                        <Select
                          onValueChange={(format) => {
                            window.open(`${BACKEND_URL}/api/clients/export/${format}`, '_blank');
                          }}
                        >
                          <SelectTrigger className="w-[120px] h-9 border-amber-400 text-amber-700 hover:bg-amber-50">
                            <Download className="h-4 w-4 mr-1" />
                            {language === 'es' ? 'Exportar' : 'Export'}
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="csv">CSV</SelectItem>
                            <SelectItem value="excel">Excel</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  
                  {/* Advanced Filters Panel */}
                  {showAdvancedFilters && (
                    <div className="bg-amber-50 border-b border-amber-200 p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {/* City Filter */}
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">{language === 'es' ? 'Ciudad' : 'City'}</label>
                          <Select value={clientCityFilter || 'all'} onValueChange={(val) => setClientCityFilter(val === 'all' ? '' : val)}>
                            <SelectTrigger className="h-8 text-sm bg-white">
                              <SelectValue placeholder={language === 'es' ? 'Todas' : 'All'} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">{language === 'es' ? 'Todas las ciudades' : 'All cities'}</SelectItem>
                              {uniqueCities.map(city => (
                                <SelectItem key={city} value={city}>{city}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Project Type Filter */}
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">{language === 'es' ? 'Tipo de Proyecto' : 'Project Type'}</label>
                          <Select value={clientProjectTypeFilter || 'all'} onValueChange={(val) => setClientProjectTypeFilter(val === 'all' ? '' : val)}>
                            <SelectTrigger className="h-8 text-sm bg-white">
                              <SelectValue placeholder={language === 'es' ? 'Todos' : 'All'} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">{language === 'es' ? 'Todos los tipos' : 'All types'}</SelectItem>
                              {uniqueProjectTypes.map(type => (
                                <SelectItem key={type} value={type}>{type}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Date From */}
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">{language === 'es' ? 'Desde' : 'From'}</label>
                          <Input 
                            type="date" 
                            value={clientDateFromFilter}
                            onChange={(e) => setClientDateFromFilter(e.target.value)}
                            className="h-8 text-sm bg-white"
                          />
                        </div>
                        
                        {/* Date To */}
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">{language === 'es' ? 'Hasta' : 'To'}</label>
                          <Input 
                            type="date" 
                            value={clientDateToFilter}
                            onChange={(e) => setClientDateToFilter(e.target.value)}
                            className="h-8 text-sm bg-white"
                          />
                        </div>
                        
                        {/* Contract Min */}
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">{language === 'es' ? 'Contrato Mín' : 'Contract Min'}</label>
                          <Input 
                            type="number" 
                            placeholder="$0"
                            value={clientContractMinFilter}
                            onChange={(e) => setClientContractMinFilter(e.target.value)}
                            className="h-8 text-sm bg-white"
                          />
                        </div>
                        
                        {/* Contract Max */}
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">{language === 'es' ? 'Contrato Máx' : 'Contract Max'}</label>
                          <Input 
                            type="number" 
                            placeholder="$999,999"
                            value={clientContractMaxFilter}
                            onChange={(e) => setClientContractMaxFilter(e.target.value)}
                            className="h-8 text-sm bg-white"
                          />
                        </div>
                      </div>
                      
                      {/* Sort Options & Clear Button */}
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-amber-200">
                        <div className="flex items-center gap-2">
                          <label className="text-xs font-medium text-gray-600">{language === 'es' ? 'Ordenar por' : 'Sort by'}</label>
                          <Select value={clientSortBy} onValueChange={setClientSortBy}>
                            <SelectTrigger className="h-8 text-sm bg-white w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="name">{language === 'es' ? 'Nombre' : 'Name'}</SelectItem>
                              <SelectItem value="date">{language === 'es' ? 'Fecha' : 'Date'}</SelectItem>
                              <SelectItem value="contract_value">{language === 'es' ? 'Valor Contrato' : 'Contract Value'}</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 px-2"
                            onClick={() => setClientSortOrder(clientSortOrder === 'asc' ? 'desc' : 'asc')}
                          >
                            {clientSortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
                          </Button>
                        </div>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-gray-600"
                          onClick={() => {
                            setClientCityFilter('');
                            setClientProjectTypeFilter('');
                            setClientDateFromFilter('');
                            setClientDateToFilter('');
                            setClientContractMinFilter('');
                            setClientContractMaxFilter('');
                            setClientSortBy('name');
                            setClientSortOrder('asc');
                          }}
                        >
                          <X className="h-3 w-3 mr-1" />
                          {language === 'es' ? 'Limpiar Filtros' : 'Clear Filters'}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <CardContent className="pt-4">
                    {loading ? (
                      <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div></div>
                    ) : filteredClients.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">{language === 'es' ? 'No hay clientes' : 'No clients found'}</div>
                    ) : (
                      <>
                        {/* Select All checkbox */}
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b">
                          <Checkbox
                            checked={selectedClients.length === filteredClients.length && filteredClients.length > 0}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedClients(filteredClients.map(c => c.id));
                              } else {
                                setSelectedClients([]);
                              }
                            }}
                          />
                          <span className="text-sm text-gray-600">
                            {language === 'es' ? 'Seleccionar todo' : 'Select all'} 
                            {selectedClients.length > 0 && ` (${selectedClients.length})`}
                          </span>
                        </div>
                        <div className="space-y-3">
                        {filteredClients.map(client => (
                          <div key={client.id} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border hover:border-amber-300 transition-colors">
                            <Checkbox
                              checked={selectedClients.includes(client.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedClients([...selectedClients, client.id]);
                                } else {
                                  setSelectedClients(selectedClients.filter(id => id !== client.id));
                                }
                              }}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <Link to={`/clients/${client.id}`} className="font-semibold text-gray-900 hover:text-amber-600 hover:underline">
                                  {client.name}
                                </Link>
                                
                                {/* Status Badges */}
                                {client.status === 'closed' && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-300 text-gray-700">Closed</span>
                                )}
                                {client.is_vip && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-200 text-amber-800">⭐ VIP</span>
                                )}
                                {(() => {
                                  if (!client.created_at) return null;
                                  const createdDate = new Date(client.created_at);
                                  const thirtyDaysAgo = new Date();
                                  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                                  return createdDate > thirtyDaysAgo ? (
                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">New</span>
                                  ) : null;
                                })()}
                                {client.is_repeat && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Repeat</span>
                                )}
                                {client.is_hot_lead && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">🔥 Hot</span>
                                )}
                                
                                {/* Financial Badges */}
                                {paymentStatus[client.id]?.outstanding > 0 && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-200 text-red-800">Overdue</span>
                                )}
                                {paymentStatus[client.id]?.paid > 0 && paymentStatus[client.id]?.outstanding === 0 && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-200 text-green-800">Paid</span>
                                )}
                                {client.is_insurance && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700">Insurance</span>
                                )}
                                
                                {/* Project Type Badge */}
                                {(() => {
                                  const projectType = client.project_category || client.project_type;
                                  const projectTypeBadges = {
                                    roofing: { bg: 'bg-red-100', text: 'text-red-700', label: 'Roofing' },
                                    kitchen: { bg: 'bg-green-100', text: 'text-green-700', label: 'Kitchen' },
                                    bathroom: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Bathroom' },
                                    flooring: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Flooring' },
                                    room_painting: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Painting' },
                                    interior_painting: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Painting' },
                                    pool_construction: { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'Pool' },
                                    hvac_replacement: { bg: 'bg-gray-200', text: 'text-gray-700', label: 'HVAC' },
                                    electrical_panel_upgrade: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Electrical' },
                                    drain_line_replacement: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Plumbing' },
                                    mold_remediation: { bg: 'bg-rose-100', text: 'text-rose-700', label: 'Mold' },
                                    water_mitigation: { bg: 'bg-sky-100', text: 'text-sky-700', label: 'Water' },
                                    drywall_repair: { bg: 'bg-stone-100', text: 'text-stone-700', label: 'Drywall' },
                                    fence_installation: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Fence' },
                                    new_construction: { bg: 'bg-yellow-200', text: 'text-yellow-800', label: 'New Build' },
                                    home_renovation: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Renovation' },
                                    home_addition: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Addition' },
                                    concrete_work: { bg: 'bg-slate-200', text: 'text-slate-700', label: 'Concrete' },
                                    stucco_repair: { bg: 'bg-neutral-200', text: 'text-neutral-700', label: 'Stucco' },
                                    window_replacement: { bg: 'bg-sky-100', text: 'text-sky-700', label: 'Windows' },
                                    tile_installation: { bg: 'bg-pink-100', text: 'text-pink-700', label: 'Tile' },
                                  };
                                  const badge = projectType ? projectTypeBadges[projectType] : null;
                                  return badge ? (
                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}>
                                      {badge.label}
                                    </span>
                                  ) : null;
                                })()}
                                
                                {/* Client Tags */}
                                {client.tags && client.tags.length > 0 && client.tags.map(tag => (
                                  <Badge 
                                    key={tag} 
                                    className="text-xs px-2 py-0 cursor-pointer hover:opacity-80"
                                    style={{ backgroundColor: getTagColor(tag), color: 'white' }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleRemoveTagFromClient(client.id, tag);
                                    }}
                                    title={language === 'es' ? 'Click para eliminar' : 'Click to remove'}
                                  >
                                    {getTagLabel(tag)} ×
                                  </Badge>
                                ))}
                                {/* Add tag button */}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-5 w-5 p-0 text-gray-400 hover:text-amber-600"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setTagModalClient(client);
                                    setShowTagModal(true);
                                  }}
                                  title={language === 'es' ? 'Añadir etiqueta' : 'Add tag'}
                                >
                                  <Tag className="h-3 w-3" />
                                </Button>
                              </div>
                              <div className="text-sm text-gray-500 flex flex-wrap gap-3">
                                {client.phone && <span className="flex items-center"><Phone className="h-3 w-3 mr-1" />{client.phone}</span>}
                                {client.email && <span className="flex items-center"><Mail className="h-3 w-3 mr-1" />{client.email}</span>}
                                {client.address && <span className="flex items-center"><MapPin className="h-3 w-3 mr-1" />{client.address}</span>}
                                {(client.project_category || client.project_type) && (
                                  <span className="flex items-center text-amber-600"><Briefcase className="h-3 w-3 mr-1" />{client.project_category || client.project_type}</span>
                                )}
                              </div>
                              {/* Quick Communication Actions */}
                              <div className="flex items-center gap-1 mt-2">
                                {client.phone && (
                                  <>
                                    <a href={`tel:${client.phone}`} title={language === 'es' ? 'Llamar' : 'Call'}>
                                      <Button size="sm" variant="outline" className="h-7 px-2 text-green-600 border-green-200 hover:bg-green-50">
                                        <Phone className="h-3 w-3 mr-1" />
                                        <span className="text-xs">{language === 'es' ? 'Llamar' : 'Call'}</span>
                                      </Button>
                                    </a>
                                    <a href={`sms:${client.phone}`} title={language === 'es' ? 'Mensaje' : 'Text'}>
                                      <Button size="sm" variant="outline" className="h-7 px-2 text-blue-600 border-blue-200 hover:bg-blue-50">
                                        <MessageSquare className="h-3 w-3 mr-1" />
                                        <span className="text-xs">{language === 'es' ? 'SMS' : 'Text'}</span>
                                      </Button>
                                    </a>
                                  </>
                                )}
                                {client.email && (
                                  <a href={`mailto:${client.email}`} title={language === 'es' ? 'Email' : 'Email'}>
                                    <Button size="sm" variant="outline" className="h-7 px-2 text-purple-600 border-purple-200 hover:bg-purple-50">
                                      <Mail className="h-3 w-3 mr-1" />
                                      <span className="text-xs">Email</span>
                                    </Button>
                                  </a>
                                )}
                              </div>
                            </div>
                            {/* Payment Status Indicator */}
                            <div className="flex flex-col items-end gap-1 min-w-[100px]">
                              {paymentStatus[client.id] && (
                                <>
                                  {getPaymentStatusBadge(client.id) && (
                                    <Badge className={`${getPaymentStatusBadge(client.id).color} ${getPaymentStatusBadge(client.id).textColor} text-xs`}>
                                      {getPaymentStatusBadge(client.id).label}
                                    </Badge>
                                  )}
                                  {paymentStatus[client.id].contract_total > 0 && (
                                    <div className="text-right">
                                      <div className="text-xs text-gray-400">
                                        {language === 'es' ? 'Contrato' : 'Contract'}
                                      </div>
                                      <div className="text-sm font-semibold text-gray-700">
                                        {formatCurrencyShort(paymentStatus[client.id].contract_total)}
                                      </div>
                                      {paymentStatus[client.id].outstanding > 0 && (
                                        <div className="text-xs text-red-500">
                                          {language === 'es' ? 'Debe' : 'Owes'}: {formatCurrencyShort(paymentStatus[client.id].outstanding)}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                              {/* Last Contact Info */}
                              {lastContactData[client.id] && (
                                <div className={`text-right mt-2 pt-2 border-t ${lastContactData[client.id].needs_followup ? 'bg-orange-50 -m-1 p-1 rounded' : ''}`}>
                                  <div className="flex items-center justify-end gap-1">
                                    <span className="text-xs">{getContactTypeIcon(lastContactData[client.id].last_contact_type)}</span>
                                    <span className={`text-xs ${lastContactData[client.id].needs_followup ? 'text-orange-600 font-semibold' : 'text-gray-400'}`}>
                                      {formatRelativeTime(lastContactData[client.id].days_since)}
                                    </span>
                                  </div>
                                  {lastContactData[client.id].needs_followup && (
                                    <div className="text-xs text-orange-500 mt-0.5">
                                      ⚠️ {language === 'es' ? 'Necesita seguimiento' : 'Needs follow-up'}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <Link to={`/clients/${client.id}`}>
                                <Button size="sm" variant="outline"><Eye className="h-4 w-4" /></Button>
                              </Link>
                              <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600" onClick={() => handleDeleteClient(client.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}

        {/* DELETE CONFIRMATION MODAL */}
        {deleteConfirmModal.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="bg-red-50 border-b border-red-100">
                <CardTitle className="text-red-600 flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  {language === 'es' ? 'Confirmar Eliminación' : 'Confirm Deletion'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-700 mb-4">
                  {language === 'es' 
                    ? `¿Está seguro que desea eliminar "${deleteConfirmModal.name}"? Esta acción no se puede deshacer.`
                    : `Are you sure you want to delete "${deleteConfirmModal.name}"? This action cannot be undone.`
                  }
                </p>
                <p className="text-sm text-gray-500 mb-3">
                  {language === 'es' 
                    ? 'Para confirmar, escriba DELETE en el campo de abajo:'
                    : 'To confirm, type DELETE in the field below:'
                  }
                </p>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="mb-4 font-mono text-center uppercase"
                  data-testid="delete-confirm-input"
                />
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setDeleteConfirmModal({ show: false, type: '', id: '', name: '' });
                      setDeleteConfirmText('');
                    }}
                  >
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button 
                    variant="destructive"
                    className={`flex-1 ${deleteConfirmText.toUpperCase() === 'DELETE' ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-300 cursor-not-allowed'}`}
                    disabled={deleteConfirmText.toUpperCase() !== 'DELETE'}
                    onClick={() => {
                      if (deleteConfirmModal.type === 'lead') {
                        handleDeleteLead(deleteConfirmModal.id);
                      } else if (deleteConfirmModal.type === 'template') {
                        handleDeleteTemplate(deleteConfirmModal.id);
                      }
                    }}
                    data-testid="delete-confirm-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Eliminar' : 'Delete'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* REPORTS TAB */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <BarChart3 className="mr-2 h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                  {language === 'es' ? 'Reportes de Estado' : 'Status Reports'}
                </h2>
                <p className="text-sm text-gray-500">{language === 'es' ? 'Genere reportes imprimibles para gestión de flujo de trabajo' : 'Generate printable reports for workflow management'}</p>
              </div>
            </div>

            {/* Report Filters */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Filter className="mr-2 h-5 w-5" />
                  {language === 'es' ? 'Filtros del Reporte' : 'Report Filters'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Report Type */}
                  <div>
                    <Label className="text-sm">{language === 'es' ? 'Tipo de Reporte' : 'Report Type'}</Label>
                    <Select value={reportType} onValueChange={setReportType}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="leads">{language === 'es' ? 'Reporte de Leads' : 'Leads Report'}</SelectItem>
                        <SelectItem value="clients">{language === 'es' ? 'Reporte de Clientes' : 'Clients Report'}</SelectItem>
                        <SelectItem value="pending_invoices">{language === 'es' ? 'Facturas Pendientes' : 'Pending Invoices'}</SelectItem>
                        <SelectItem value="gross_proposals">{language === 'es' ? 'Propuestas Brutas' : 'Gross Proposals'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Date From */}
                  <div>
                    <Label className="text-sm">{language === 'es' ? 'Fecha Desde' : 'Date From'}</Label>
                    <Input 
                      type="date" 
                      value={reportDateFrom} 
                      onChange={(e) => setReportDateFrom(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Date To */}
                  <div>
                    <Label className="text-sm">{language === 'es' ? 'Fecha Hasta' : 'Date To'}</Label>
                    <Input 
                      type="date" 
                      value={reportDateTo} 
                      onChange={(e) => setReportDateTo(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Status Filter */}
                  <div>
                    <Label className="text-sm">{language === 'es' ? 'Estado' : 'Status'}</Label>
                    <Select value={reportStatusFilter} onValueChange={setReportStatusFilter}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{language === 'es' ? 'Todos' : 'All'}</SelectItem>
                        {reportType === 'leads' ? (
                          <>
                            <SelectItem value="estimate_provided">{language === 'es' ? 'Propuesta Enviada' : 'Proposal Sent'}</SelectItem>
                            <SelectItem value="follow_up">{language === 'es' ? 'Seguimiento' : 'Follow Up'}</SelectItem>
                            <SelectItem value="presentation_made">{language === 'es' ? 'Presentado' : 'Presented'}</SelectItem>
                            <SelectItem value="lead_won">{language === 'es' ? 'Ganado' : 'Won'}</SelectItem>
                            <SelectItem value="lead_lost">{language === 'es' ? 'Perdido' : 'Lost'}</SelectItem>
                          </>
                        ) : (
                          <>
                            <SelectItem value="permit_approved">{language === 'es' ? 'Permiso Aprobado' : 'Permit Approved'}</SelectItem>
                            <SelectItem value="first_inspection">{language === 'es' ? '1ra Inspección' : '1st Inspection'}</SelectItem>
                            <SelectItem value="second_inspection">{language === 'es' ? '2da Inspección' : '2nd Inspection'}</SelectItem>
                            <SelectItem value="final_inspection">{language === 'es' ? '3ra y Final' : '3rd & Final'}</SelectItem>
                            <SelectItem value="payment_received">{language === 'es' ? 'Pago Recibido' : 'Payment Received'}</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button 
                    onClick={() => {
                      setReportDateFrom('');
                      setReportDateTo('');
                      setReportStatusFilter('all');
                    }}
                    variant="outline"
                  >
                    {language === 'es' ? 'Limpiar Filtros' : 'Clear Filters'}
                  </Button>
                  <Button 
                    onClick={() => window.print()}
                    variant="outline"
                    className="border-gray-300"
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    {language === 'es' ? 'Imprimir' : 'Print'}
                  </Button>
                  <Button 
                    onClick={saveReportToPdf}
                    disabled={savingReportPdf}
                    style={{ backgroundColor: COMPANY.primaryColor }}
                    className="text-white"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {savingReportPdf ? '...' : (language === 'es' ? 'Guardar PDF' : 'Save PDF')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Report Preview */}
            <Card ref={reportRef} id="printable-report" className="print:shadow-none print:border-none">
              <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg print:bg-gray-800">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl">
                      {reportType === 'leads' && (language === 'es' ? 'Reporte de Estado de Leads' : 'Leads Status Report')}
                      {reportType === 'clients' && (language === 'es' ? 'Reporte de Estado de Clientes' : 'Clients Status Report')}
                      {reportType === 'pending_invoices' && (language === 'es' ? 'Reporte de Facturas Pendientes' : 'Pending Invoices Report')}
                      {reportType === 'gross_proposals' && (language === 'es' ? 'Reporte de Propuestas Brutas' : 'Gross Proposals Report')}
                    </CardTitle>
                    <p className="text-sm opacity-90 mt-1">
                      {COMPANY.name} • {new Date().toLocaleDateString()}
                      {(reportDateFrom || reportDateTo) && (
                        <span className="ml-2">
                          | {reportDateFrom || '...'} - {reportDateTo || '...'}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      {reportType === 'leads' && leads.filter(l => {
                        const matchesStatus = reportStatusFilter === 'all' || l.lead_status === reportStatusFilter;
                        const createdDate = l.created_at ? new Date(l.created_at) : null;
                        const matchesDateFrom = !reportDateFrom || (createdDate && createdDate >= new Date(reportDateFrom));
                        const matchesDateTo = !reportDateTo || (createdDate && createdDate <= new Date(reportDateTo + 'T23:59:59'));
                        return matchesStatus && matchesDateFrom && matchesDateTo;
                      }).length}
                      {reportType === 'clients' && clients.filter(c => {
                        const matchesStatus = reportStatusFilter === 'all' || c.project_stage === reportStatusFilter;
                        const createdDate = c.created_at ? new Date(c.created_at) : null;
                        const matchesDateFrom = !reportDateFrom || (createdDate && createdDate >= new Date(reportDateFrom));
                        const matchesDateTo = !reportDateTo || (createdDate && createdDate <= new Date(reportDateTo + 'T23:59:59'));
                        return matchesStatus && matchesDateFrom && matchesDateTo;
                      }).length}
                      {reportType === 'pending_invoices' && leads.filter(l => {
                        const hasPendingInvoice = l.invoice && l.invoice.status !== 'paid';
                        const createdDate = l.created_at ? new Date(l.created_at) : null;
                        const matchesDateFrom = !reportDateFrom || (createdDate && createdDate >= new Date(reportDateFrom));
                        const matchesDateTo = !reportDateTo || (createdDate && createdDate <= new Date(reportDateTo + 'T23:59:59'));
                        return hasPendingInvoice && matchesDateFrom && matchesDateTo;
                      }).length}
                      {reportType === 'gross_proposals' && leads.filter(l => {
                        const createdDate = l.created_at ? new Date(l.created_at) : null;
                        const matchesDateFrom = !reportDateFrom || (createdDate && createdDate >= new Date(reportDateFrom));
                        const matchesDateTo = !reportDateTo || (createdDate && createdDate <= new Date(reportDateTo + 'T23:59:59'));
                        return matchesDateFrom && matchesDateTo;
                      }).length}
                    </p>
                    <p className="text-xs opacity-75">{language === 'es' ? 'Total Registros' : 'Total Records'}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {reportType === 'leads' && (
                  /* LEADS REPORT TABLE */
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-semibold">#</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Cliente' : 'Client'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Contacto' : 'Contact'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Proyecto' : 'Project'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Estado' : 'Status'}</th>
                          <th className="text-right p-3 font-semibold">{language === 'es' ? 'Valor' : 'Value'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Fecha' : 'Date'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads
                          .filter(l => {
                            const matchesStatus = reportStatusFilter === 'all' || l.lead_status === reportStatusFilter;
                            const createdDate = l.created_at ? new Date(l.created_at) : null;
                            const matchesDateFrom = !reportDateFrom || (createdDate && createdDate >= new Date(reportDateFrom));
                            const matchesDateTo = !reportDateTo || (createdDate && createdDate <= new Date(reportDateTo + 'T23:59:59'));
                            return matchesStatus && matchesDateFrom && matchesDateTo;
                          })
                          .map((lead, index) => (
                            <tr key={lead.id} className="border-b hover:bg-gray-50">
                              <td className="p-3 text-gray-500">{index + 1}</td>
                              <td className="p-3">
                                <div className="font-medium">{lead.client_name}</div>
                                <div className="text-xs text-gray-500">{lead.estimate_number}</div>
                              </td>
                              <td className="p-3">
                                <div className="text-xs">{lead.client_email}</div>
                                <div className="text-xs text-gray-500">{lead.client_phone}</div>
                              </td>
                              <td className="p-3">
                                <div className="text-xs">{projectTypes.find(p => p.value === lead.project_type)?.label || lead.project_type}</div>
                                <div className="text-xs text-gray-500 truncate max-w-[150px]">{lead.client_address}</div>
                              </td>
                              <td className="p-3">{getLeadStatusBadge(lead.lead_status)}</td>
                              <td className="p-3 text-right font-medium" style={{ color: COMPANY.primaryColor }}>
                                {formatCurrency(lead.grand_total)}
                              </td>
                              <td className="p-3 text-xs text-gray-500">
                                {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                          ))
                        }
                      </tbody>
                      <tfoot className="bg-gray-100 font-semibold">
                        <tr>
                          <td colSpan="5" className="p-3 text-right">{language === 'es' ? 'Total:' : 'Total:'}</td>
                          <td className="p-3 text-right" style={{ color: COMPANY.primaryColor }}>
                            {formatCurrency(
                              leads
                                .filter(l => {
                                  const matchesStatus = reportStatusFilter === 'all' || l.lead_status === reportStatusFilter;
                                  const createdDate = l.created_at ? new Date(l.created_at) : null;
                                  const matchesDateFrom = !reportDateFrom || (createdDate && createdDate >= new Date(reportDateFrom));
                                  const matchesDateTo = !reportDateTo || (createdDate && createdDate <= new Date(reportDateTo + 'T23:59:59'));
                                  return matchesStatus && matchesDateFrom && matchesDateTo;
                                })
                                .reduce((sum, l) => sum + (l.grand_total || 0), 0)
                            )}
                          </td>
                          <td className="p-3"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
                
                {reportType === 'clients' && (
                  /* CLIENTS REPORT TABLE */
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-semibold">#</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Cliente' : 'Client'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Contacto' : 'Contact'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Dirección' : 'Address'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Etapa del Proyecto' : 'Project Stage'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Fecha' : 'Date'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clients
                          .filter(c => {
                            const matchesStatus = reportStatusFilter === 'all' || c.project_stage === reportStatusFilter;
                            const createdDate = c.created_at ? new Date(c.created_at) : null;
                            const matchesDateFrom = !reportDateFrom || (createdDate && createdDate >= new Date(reportDateFrom));
                            const matchesDateTo = !reportDateTo || (createdDate && createdDate <= new Date(reportDateTo + 'T23:59:59'));
                            return matchesStatus && matchesDateFrom && matchesDateTo;
                          })
                          .map((client, index) => {
                            const stageConfig = {
                              permit_approved: { label: language === 'es' ? 'Permiso Aprobado' : 'Permit Approved', color: 'bg-orange-100 text-orange-700' },
                              first_inspection: { label: language === 'es' ? '1ra Inspección' : '1st Inspection', color: 'bg-yellow-100 text-yellow-700' },
                              second_inspection: { label: language === 'es' ? '2da Inspección' : '2nd Inspection', color: 'bg-lime-100 text-lime-700' },
                              final_inspection: { label: language === 'es' ? '3ra y Final' : '3rd & Final', color: 'bg-green-100 text-green-700' },
                              payment_received: { label: language === 'es' ? 'Pago Recibido' : 'Payment Received', color: 'bg-emerald-100 text-emerald-700' }
                            };
                            const stage = stageConfig[client.project_stage] || stageConfig.permit_approved;
                            return (
                              <tr key={client.id} className="border-b hover:bg-gray-50">
                                <td className="p-3 text-gray-500">{index + 1}</td>
                                <td className="p-3">
                                  <div className="font-medium">{client.name}</div>
                                </td>
                                <td className="p-3">
                                  <div className="text-xs">{client.email}</div>
                                  <div className="text-xs text-gray-500">{client.phone}</div>
                                </td>
                                <td className="p-3">
                                  <div className="text-xs truncate max-w-[200px]">{client.address || '-'}</div>
                                </td>
                                <td className="p-3">
                                  <Badge variant="outline" className={stage.color}>{stage.label}</Badge>
                                </td>
                                <td className="p-3 text-xs text-gray-500">
                                  {client.created_at ? new Date(client.created_at).toLocaleDateString() : '-'}
                                </td>
                              </tr>
                            );
                          })
                        }
                      </tbody>
                    </table>
                  </div>
                )}

                {reportType === 'pending_invoices' && (
                  /* PENDING INVOICES REPORT TABLE */
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-semibold">#</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Factura #' : 'Invoice #'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Cliente' : 'Client'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Contacto' : 'Contact'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Estado' : 'Status'}</th>
                          <th className="text-right p-3 font-semibold">{language === 'es' ? 'Monto' : 'Amount'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Vencimiento' : 'Due Date'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads
                          .filter(l => {
                            const hasPendingInvoice = l.invoice && l.invoice.status !== 'paid';
                            const createdDate = l.created_at ? new Date(l.created_at) : null;
                            const matchesDateFrom = !reportDateFrom || (createdDate && createdDate >= new Date(reportDateFrom));
                            const matchesDateTo = !reportDateTo || (createdDate && createdDate <= new Date(reportDateTo + 'T23:59:59'));
                            return hasPendingInvoice && matchesDateFrom && matchesDateTo;
                          })
                          .map((lead, index) => {
                            const invoice = lead.invoice || {};
                            const statusConfig = {
                              draft: { label: language === 'es' ? 'Borrador' : 'Draft', color: 'bg-gray-100 text-gray-700' },
                              sent: { label: language === 'es' ? 'Enviada' : 'Sent', color: 'bg-blue-100 text-blue-700' },
                              overdue: { label: language === 'es' ? 'Vencida' : 'Overdue', color: 'bg-red-100 text-red-700' },
                              partial: { label: language === 'es' ? 'Parcial' : 'Partial', color: 'bg-yellow-100 text-yellow-700' }
                            };
                            const status = statusConfig[invoice.status] || statusConfig.draft;
                            return (
                              <tr key={lead.id} className="border-b hover:bg-gray-50">
                                <td className="p-3 text-gray-500">{index + 1}</td>
                                <td className="p-3 font-medium">{invoice.invoice_number || lead.proposal_id || '-'}</td>
                                <td className="p-3">
                                  <div className="font-medium">{lead.client_name}</div>
                                </td>
                                <td className="p-3">
                                  <div className="text-xs">{lead.client_email}</div>
                                  <div className="text-xs text-gray-500">{lead.client_phone}</div>
                                </td>
                                <td className="p-3">
                                  <Badge variant="outline" className={status.color}>{status.label}</Badge>
                                </td>
                                <td className="p-3 text-right font-semibold">
                                  ${(invoice.balance_due || lead.grand_total || 0).toLocaleString()}
                                </td>
                                <td className="p-3 text-xs text-gray-500">
                                  {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '-'}
                                </td>
                              </tr>
                            );
                          })
                        }
                      </tbody>
                      <tfoot className="bg-amber-50 font-semibold">
                        <tr>
                          <td colSpan="5" className="p-3 text-right">{language === 'es' ? 'Total Pendiente:' : 'Total Pending:'}</td>
                          <td className="p-3 text-right text-lg" style={{ color: COMPANY.primaryColor }}>
                            ${leads
                              .filter(l => l.invoice && l.invoice.status !== 'paid')
                              .reduce((sum, l) => sum + (l.invoice?.balance_due || l.grand_total || 0), 0)
                              .toLocaleString()}
                          </td>
                          <td className="p-3"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {reportType === 'gross_proposals' && (
                  /* GROSS PROPOSALS REPORT TABLE */
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-semibold">#</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Propuesta #' : 'Proposal #'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Cliente' : 'Client'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Proyecto' : 'Project'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Tipo' : 'Type'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Estado' : 'Status'}</th>
                          <th className="text-right p-3 font-semibold">{language === 'es' ? 'Subtotal' : 'Subtotal'}</th>
                          <th className="text-right p-3 font-semibold">{language === 'es' ? 'Impuesto' : 'Tax'}</th>
                          <th className="text-right p-3 font-semibold">{language === 'es' ? 'Total' : 'Total'}</th>
                          <th className="text-left p-3 font-semibold">{language === 'es' ? 'Fecha' : 'Date'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads
                          .filter(l => {
                            const createdDate = l.created_at ? new Date(l.created_at) : null;
                            const matchesDateFrom = !reportDateFrom || (createdDate && createdDate >= new Date(reportDateFrom));
                            const matchesDateTo = !reportDateTo || (createdDate && createdDate <= new Date(reportDateTo + 'T23:59:59'));
                            return matchesDateFrom && matchesDateTo;
                          })
                          .map((lead, index) => {
                            const projectTypes = {
                              new_construction: language === 'es' ? 'Nueva Construcción' : 'New Construction',
                              renovation: language === 'es' ? 'Renovación' : 'Renovation',
                              repair: language === 'es' ? 'Reparación' : 'Repair',
                              commercial: language === 'es' ? 'Comercial' : 'Commercial',
                              residential: language === 'es' ? 'Residencial' : 'Residential'
                            };
                            const statusConfig = {
                              estimate_provided: { label: language === 'es' ? 'Propuesta' : 'Proposal', color: 'bg-blue-100 text-blue-700' },
                              follow_up: { label: language === 'es' ? 'Seguimiento' : 'Follow Up', color: 'bg-purple-100 text-purple-700' },
                              lead_won: { label: language === 'es' ? 'Ganado' : 'Won', color: 'bg-green-100 text-green-700' },
                              lead_lost: { label: language === 'es' ? 'Perdido' : 'Lost', color: 'bg-red-100 text-red-700' }
                            };
                            const status = statusConfig[lead.lead_status] || statusConfig.estimate_provided;
                            const subtotal = lead.estimate_type === 'lump_sum' 
                              ? (lead.lump_sum_total || 0)
                              : (lead.line_items || []).reduce((sum, item) => sum + (item.total || 0), 0) + (lead.general_fees || []).reduce((sum, fee) => sum + (fee.price || 0), 0);
                            const taxAmount = lead.tax_amount || (subtotal * (lead.tax_rate || 7) / 100);
                            const total = lead.grand_total || (subtotal + taxAmount);
                            
                            return (
                              <tr key={lead.id} className="border-b hover:bg-gray-50">
                                <td className="p-3 text-gray-500">{index + 1}</td>
                                <td className="p-3 font-medium">{lead.proposal_id || lead.estimate_number || '-'}</td>
                                <td className="p-3">
                                  <div className="font-medium">{lead.client_name}</div>
                                  <div className="text-xs text-gray-500">{lead.client_phone}</div>
                                </td>
                                <td className="p-3 text-xs">
                                  {projectTypes[lead.project_category] || projectTypes[lead.project_type] || '-'}
                                </td>
                                <td className="p-3 text-xs">
                                  {lead.estimate_type === 'lump_sum' ? 'Lump Sum' : 'Itemized'}
                                </td>
                                <td className="p-3">
                                  <Badge variant="outline" className={status.color}>{status.label}</Badge>
                                </td>
                                <td className="p-3 text-right">${subtotal.toLocaleString()}</td>
                                <td className="p-3 text-right text-gray-500">${taxAmount.toLocaleString()}</td>
                                <td className="p-3 text-right font-semibold">${total.toLocaleString()}</td>
                                <td className="p-3 text-xs text-gray-500">
                                  {lead.estimate_date ? new Date(lead.estimate_date).toLocaleDateString() : (lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-')}
                                </td>
                              </tr>
                            );
                          })
                        }
                      </tbody>
                      <tfoot className="bg-amber-50 font-semibold">
                        <tr>
                          <td colSpan="6" className="p-3 text-right">{language === 'es' ? 'Totales:' : 'Totals:'}</td>
                          <td className="p-3 text-right">
                            ${leads
                              .filter(l => {
                                const createdDate = l.created_at ? new Date(l.created_at) : null;
                                const matchesDateFrom = !reportDateFrom || (createdDate && createdDate >= new Date(reportDateFrom));
                                const matchesDateTo = !reportDateTo || (createdDate && createdDate <= new Date(reportDateTo + 'T23:59:59'));
                                return matchesDateFrom && matchesDateTo;
                              })
                              .reduce((sum, l) => {
                                const subtotal = l.estimate_type === 'lump_sum' 
                                  ? (l.lump_sum_total || 0)
                                  : (l.line_items || []).reduce((s, item) => s + (item.total || 0), 0) + (l.general_fees || []).reduce((s, fee) => s + (fee.price || 0), 0);
                                return sum + subtotal;
                              }, 0)
                              .toLocaleString()}
                          </td>
                          <td className="p-3 text-right text-gray-500">
                            ${leads
                              .filter(l => {
                                const createdDate = l.created_at ? new Date(l.created_at) : null;
                                const matchesDateFrom = !reportDateFrom || (createdDate && createdDate >= new Date(reportDateFrom));
                                const matchesDateTo = !reportDateTo || (createdDate && createdDate <= new Date(reportDateTo + 'T23:59:59'));
                                return matchesDateFrom && matchesDateTo;
                              })
                              .reduce((sum, l) => sum + (l.tax_amount || 0), 0)
                              .toLocaleString()}
                          </td>
                          <td className="p-3 text-right text-lg" style={{ color: COMPANY.primaryColor }}>
                            ${leads
                              .filter(l => {
                                const createdDate = l.created_at ? new Date(l.created_at) : null;
                                const matchesDateFrom = !reportDateFrom || (createdDate && createdDate >= new Date(reportDateFrom));
                                const matchesDateTo = !reportDateTo || (createdDate && createdDate <= new Date(reportDateTo + 'T23:59:59'));
                                return matchesDateFrom && matchesDateTo;
                              })
                              .reduce((sum, l) => sum + (l.grand_total || 0), 0)
                              .toLocaleString()}
                          </td>
                          <td className="p-3"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
                
                {/* Report Summary */}
                <div className="p-4 bg-gray-50 border-t">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                    {reportType === 'leads' && (
                      <>
                        <div>
                          <div className="text-2xl font-bold text-blue-600">
                            {leads.filter(l => l.lead_status === 'estimate_provided').length}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Propuestas' : 'Proposals'}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-purple-600">
                            {leads.filter(l => l.lead_status === 'follow_up').length}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Seguimiento' : 'Follow Up'}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600">
                            {leads.filter(l => l.lead_status === 'lead_won').length}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Ganados' : 'Won'}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-red-600">
                            {leads.filter(l => l.lead_status === 'lead_lost').length}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Perdidos' : 'Lost'}</div>
                        </div>
                      </>
                    )}
                    {reportType === 'clients' && (
                      <>
                        <div>
                          <div className="text-2xl font-bold text-orange-600">
                            {clients.filter(c => !c.project_stage || c.project_stage === 'permit_approved').length}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Permiso' : 'Permit'}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-yellow-600">
                            {clients.filter(c => c.project_stage === 'first_inspection' || c.project_stage === 'second_inspection').length}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Inspecciones' : 'Inspections'}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600">
                            {clients.filter(c => c.project_stage === 'final_inspection').length}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Final' : 'Final'}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold" style={{ color: COMPANY.primaryColor }}>
                            {clients.filter(c => c.project_stage === 'payment_received').length}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Pagado' : 'Paid'}</div>
                        </div>
                      </>
                    )}
                    {reportType === 'pending_invoices' && (
                      <>
                        <div>
                          <div className="text-2xl font-bold text-gray-600">
                            {leads.filter(l => l.invoice?.status === 'draft').length}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Borrador' : 'Draft'}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-blue-600">
                            {leads.filter(l => l.invoice?.status === 'sent').length}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Enviadas' : 'Sent'}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-red-600">
                            {leads.filter(l => l.invoice?.status === 'overdue').length}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Vencidas' : 'Overdue'}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold" style={{ color: COMPANY.primaryColor }}>
                            ${leads.filter(l => l.invoice && l.invoice.status !== 'paid').reduce((sum, l) => sum + (l.invoice?.balance_due || l.grand_total || 0), 0).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Total Pendiente' : 'Total Pending'}</div>
                        </div>
                      </>
                    )}
                    {reportType === 'gross_proposals' && (
                      <>
                        <div>
                          <div className="text-2xl font-bold text-blue-600">
                            {leads.length}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Propuestas' : 'Proposals'}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600">
                            {leads.filter(l => l.lead_status === 'lead_won').length}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Ganadas' : 'Won'}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-purple-600">
                            {((leads.filter(l => l.lead_status === 'lead_won').length / Math.max(leads.length, 1)) * 100).toFixed(0)}%
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Tasa Conversión' : 'Conversion Rate'}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold" style={{ color: COMPANY.primaryColor }}>
                            ${leads.reduce((sum, l) => sum + (l.grand_total || 0), 0).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">{language === 'es' ? 'Valor Total' : 'Total Value'}</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Header with Date Range */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <BarChart3 className="mr-2 h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                  {language === 'es' ? 'Panel de Analíticas' : 'Analytics Dashboard'}
                </h2>
                <p className="text-sm text-gray-500">
                  {language === 'es' ? 'Resumen del rendimiento de su negocio' : 'Overview of your business performance'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 bg-white rounded-lg border p-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <Input
                    type="date"
                    value={analyticsDateFrom}
                    onChange={(e) => setAnalyticsDateFrom(e.target.value)}
                    className="w-36 h-8 text-sm"
                  />
                  <span className="text-gray-400">→</span>
                  <Input
                    type="date"
                    value={analyticsDateTo}
                    onChange={(e) => setAnalyticsDateTo(e.target.value)}
                    className="w-36 h-8 text-sm"
                  />
                </div>
                <Button
                  onClick={saveAnalyticsToPdf}
                  disabled={savingAnalyticsPdf}
                  style={{ backgroundColor: COMPANY.primaryColor }}
                  className="text-white"
                  data-testid="download-analytics-pdf-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {savingAnalyticsPdf ? '...' : (language === 'es' ? 'Descargar PDF' : 'Download PDF')}
                </Button>
              </div>
            </div>

            {/* Analytics Content - wrapped in ref for PDF export */}
            <div ref={analyticsRef} className="space-y-6 bg-white p-4 rounded-lg">
            {/* PDF Header - Shows date range info */}
            <div className="text-center pb-4 border-b border-gray-200 mb-4">
              <h1 className="text-2xl font-bold" style={{ color: COMPANY.primaryColor }}>
                {language === 'es' ? 'Reporte de Analíticas' : 'Analytics Report'}
              </h1>
              <p className="text-gray-600 mt-1">
                {language === 'es' ? 'Período:' : 'Period:'} {analyticsDateFrom} {language === 'es' ? 'a' : 'to'} {analyticsDateTo}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                {language === 'es' ? 'Generado:' : 'Generated:'} {new Date().toLocaleDateString()}
              </p>
            </div>
            {/* Key Metrics Cards */}
            {(() => {
              // Filter leads by date range
              const filteredLeads = leads.filter(l => {
                const leadDate = new Date(l.created_at || l.createdAt);
                const from = analyticsDateFrom ? new Date(analyticsDateFrom) : null;
                const to = analyticsDateTo ? new Date(analyticsDateTo + 'T23:59:59') : null;
                if (from && leadDate < from) return false;
                if (to && leadDate > to) return false;
                return true;
              });

              // Calculate metrics
              const totalEstimates = filteredLeads.length;
              const totalPipelineValue = filteredLeads.reduce((sum, l) => sum + (l.grand_total || l.total || 0), 0);
              const wonLeads = filteredLeads.filter(l => l.lead_status === 'lead_won');
              const lostLeads = filteredLeads.filter(l => l.lead_status === 'lead_lost');
              const wonValue = wonLeads.reduce((sum, l) => sum + (l.grand_total || l.total || 0), 0);
              const avgEstimateValue = totalEstimates > 0 ? totalPipelineValue / totalEstimates : 0;
              const conversionRate = (wonLeads.length + lostLeads.length) > 0 
                ? (wonLeads.length / (wonLeads.length + lostLeads.length) * 100) 
                : 0;

              // Status counts
              const statusCounts = {
                new: filteredLeads.filter(l => l.lead_status === 'new' || !l.lead_status).length,
                estimate_provided: filteredLeads.filter(l => l.lead_status === 'estimate_provided').length,
                follow_up: filteredLeads.filter(l => l.lead_status === 'follow_up').length,
                presentation_made: filteredLeads.filter(l => l.lead_status === 'presentation_made').length,
                lead_won: wonLeads.length,
                lead_lost: lostLeads.length
              };

              // Project type counts
              const projectTypeCounts = {};
              filteredLeads.forEach(l => {
                const type = l.project_type || 'Other';
                projectTypeCounts[type] = (projectTypeCounts[type] || 0) + 1;
              });
              const sortedProjectTypes = Object.entries(projectTypeCounts)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 8);

              // Project type labels
              const projectTypeLabels = {
                'tile_roof': 'Tile Roof',
                'shingle_roof': 'Shingle Roof',
                'metal_roof': 'Metal Roof',
                'flat_roof': 'Flat Roof',
                'kitchen': 'Kitchen',
                'bathroom': 'Bathroom',
                'pool': 'Pool',
                'home_renovation': 'Renovation',
                'flooring': 'Flooring',
                'hvac': 'HVAC',
                'windows': 'Windows',
                'painting': 'Painting',
                'concrete': 'Concrete',
                'fence': 'Fence',
                'gutter': 'Gutters',
                'electrical': 'Electrical'
              };

              // Colors for charts
              const statusColors = {
                new: '#6366f1',
                estimate_provided: '#f59e0b',
                follow_up: '#3b82f6',
                presentation_made: '#8b5cf6',
                lead_won: '#10b981',
                lead_lost: '#ef4444'
              };

              const projectColors = ['#f97316', '#06b6d4', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b', '#6366f1', '#84cc16'];

              return (
                <>
                  {/* Top Metrics Row */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Total Proposals */}
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-blue-600 font-medium">{language === 'es' ? 'Total Propuestas' : 'Total Proposals'}</p>
                            <p className="text-3xl font-bold text-blue-700">{totalEstimates}</p>
                          </div>
                          <div className="w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center">
                            <FileText className="h-6 w-6 text-blue-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pipeline Value */}
                    <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-green-600 font-medium">{language === 'es' ? 'Valor Pipeline' : 'Pipeline Value'}</p>
                            <p className="text-3xl font-bold text-green-700">${totalPipelineValue.toLocaleString()}</p>
                          </div>
                          <div className="w-12 h-12 rounded-full bg-green-200 flex items-center justify-center">
                            <DollarSign className="h-6 w-6 text-green-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Won Revenue */}
                    <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-emerald-600 font-medium">{language === 'es' ? 'Ingresos Ganados' : 'Won Revenue'}</p>
                            <p className="text-3xl font-bold text-emerald-700">${wonValue.toLocaleString()}</p>
                          </div>
                          <div className="w-12 h-12 rounded-full bg-emerald-200 flex items-center justify-center">
                            <TrendingUp className="h-6 w-6 text-emerald-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Conversion Rate */}
                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-purple-600 font-medium">{language === 'es' ? 'Tasa Conversión' : 'Conversion Rate'}</p>
                            <p className="text-3xl font-bold text-purple-700">{conversionRate.toFixed(1)}%</p>
                          </div>
                          <div className="w-12 h-12 rounded-full bg-purple-200 flex items-center justify-center">
                            <Activity className="h-6 w-6 text-purple-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Leads by Status - Horizontal Bar Chart */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center">
                          <PieChart className="mr-2 h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                          {language === 'es' ? 'Leads por Estado' : 'Leads by Status'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {[
                            { key: 'new', label: language === 'es' ? 'Nuevo' : 'New' },
                            { key: 'estimate_provided', label: language === 'es' ? 'Propuesta Enviada' : 'Proposal Sent' },
                            { key: 'follow_up', label: language === 'es' ? 'Seguimiento' : 'Follow Up' },
                            { key: 'presentation_made', label: language === 'es' ? 'Presentación' : 'Presented' },
                            { key: 'lead_won', label: language === 'es' ? 'Ganado' : 'Won' },
                            { key: 'lead_lost', label: language === 'es' ? 'Perdido' : 'Lost' }
                          ].map(status => {
                            const count = statusCounts[status.key] || 0;
                            const percentage = totalEstimates > 0 ? (count / totalEstimates * 100) : 0;
                            return (
                              <div key={status.key} className="flex items-center gap-3">
                                <div className="w-24 text-sm text-gray-600 truncate">{status.label}</div>
                                <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full rounded-full transition-all duration-500"
                                    style={{ 
                                      width: `${percentage}%`,
                                      backgroundColor: statusColors[status.key]
                                    }}
                                  />
                                </div>
                                <div className="w-12 text-right text-sm font-semibold" style={{ color: statusColors[status.key] }}>
                                  {count}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-4 pt-4 border-t flex justify-between text-sm text-gray-500">
                          <span>{language === 'es' ? 'Total en período' : 'Total in period'}: <strong className="text-gray-800">{totalEstimates}</strong></span>
                          <span>{language === 'es' ? 'Ganados' : 'Won'}: <strong className="text-green-600">{statusCounts.lead_won}</strong></span>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Projects by Category - Horizontal Bar Chart */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center">
                          <BarChart3 className="mr-2 h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                          {language === 'es' ? 'Proyectos por Categoría' : 'Projects by Category'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {sortedProjectTypes.length > 0 ? (
                          <div className="space-y-3">
                            {sortedProjectTypes.map(([type, count], index) => {
                              const percentage = totalEstimates > 0 ? (count / totalEstimates * 100) : 0;
                              const label = projectTypeLabels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                              return (
                                <div key={type} className="flex items-center gap-3">
                                  <div className="w-24 text-sm text-gray-600 truncate" title={label}>{label}</div>
                                  <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full rounded-full transition-all duration-500"
                                      style={{ 
                                        width: `${percentage}%`,
                                        backgroundColor: projectColors[index % projectColors.length]
                                      }}
                                    />
                                  </div>
                                  <div className="w-12 text-right text-sm font-semibold" style={{ color: projectColors[index % projectColors.length] }}>
                                    {count}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-400">
                            <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>{language === 'es' ? 'Sin datos en este período' : 'No data in this period'}</p>
                          </div>
                        )}
                        <div className="mt-4 pt-4 border-t text-sm text-gray-500">
                          <span>{language === 'es' ? 'Categorías activas' : 'Active categories'}: <strong className="text-gray-800">{Object.keys(projectTypeCounts).length}</strong></span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Bottom Stats Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Average Proposal Value */}
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center">
                            <DollarSign className="h-7 w-7 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">{language === 'es' ? 'Valor Promedio' : 'Avg. Proposal'}</p>
                            <p className="text-2xl font-bold text-gray-800">${avgEstimateValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Won vs Lost */}
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center">
                            <UserCheck className="h-7 w-7 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-500">{language === 'es' ? 'Ganados vs Perdidos' : 'Won vs Lost'}</p>
                            <div className="flex items-center gap-3">
                              <span className="text-xl font-bold text-green-600 flex items-center">
                                <ArrowUpRight className="h-4 w-4" />{statusCounts.lead_won}
                              </span>
                              <span className="text-gray-300">/</span>
                              <span className="text-xl font-bold text-red-500 flex items-center">
                                <ArrowDownRight className="h-4 w-4" />{statusCounts.lead_lost}
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pending Follow-ups */}
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center">
                            <Clock className="h-7 w-7 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">{language === 'es' ? 'Requieren Seguimiento' : 'Need Follow-up'}</p>
                            <p className="text-2xl font-bold text-gray-800">{statusCounts.follow_up + statusCounts.estimate_provided}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </>
              );
            })()}
            </div>
          </div>
        )}

        {/* PORTAL USERS TAB */}
        {activeTab === 'portal' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <Home className="mr-2 h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                  {language === 'es' ? 'Usuarios del Portal' : 'Portal Users'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {language === 'es' 
                    ? 'Clientes que se han registrado en el portal de propietarios' 
                    : 'Clients who have registered on the homeowner portal'}
                </p>
              </div>
              <Button onClick={fetchData} variant="outline">
                <RefreshCw className="mr-2 h-4 w-4" /> {language === 'es' ? 'Actualizar' : 'Refresh'}
              </Button>
            </div>

            {homeowners.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Home className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    {language === 'es' ? 'Sin usuarios registrados' : 'No Registered Users'}
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    {language === 'es' 
                      ? 'Los clientes pueden registrarse en el portal de propietarios para ver el progreso de sus proyectos.' 
                      : 'Clients can register on the homeowner portal to view their project progress.'}
                  </p>
                  <div className="mt-4 p-4 bg-amber-50 rounded-lg max-w-md mx-auto">
                    <p className="text-sm text-amber-800">
                      <strong>{language === 'es' ? 'URL del Portal:' : 'Portal URL:'}</strong><br />
                      <code className="text-xs bg-amber-100 px-2 py-1 rounded">{window.location.origin}/homeowner/register</code>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {homeowners.map(homeowner => {
                  // Find matching client by email for linking
                  const matchingClient = clients.find(c => c.email?.toLowerCase() === homeowner.email?.toLowerCase());
                  return (
                    <Card key={homeowner.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: COMPANY.primaryColor }}>
                              {homeowner.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              {matchingClient ? (
                                <Link 
                                  to={`/clients/${matchingClient.id}`} 
                                  className="font-semibold text-gray-800 hover:underline"
                                  style={{ color: COMPANY.primaryColor }}
                                >
                                  {homeowner.name}
                                </Link>
                              ) : (
                                <h3 className="font-semibold text-gray-800">{homeowner.name}</h3>
                              )}
                              <p className="text-sm text-gray-500">{homeowner.email}</p>
                              {matchingClient && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1">
                                  <Users className="h-3 w-3 mr-1" />
                                  {language === 'es' ? 'Cliente Vinculado' : 'Linked Client'}
                                </span>
                              )}
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                            onClick={() => handleDeleteHomeowner(homeowner.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="mt-4 space-y-2 text-sm">
                          {homeowner.phone && (
                            <div className="flex items-center text-gray-600">
                              <Phone className="h-4 w-4 mr-2 text-gray-400" />
                              {homeowner.phone}
                            </div>
                          )}
                          {homeowner.address && (
                            <div className="flex items-center text-gray-600">
                              <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                              <span className="truncate">{homeowner.address}</span>
                            </div>
                          )}
                          <div className="flex items-center text-gray-400 text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            {language === 'es' ? 'Registrado:' : 'Registered:'} {new Date(homeowner.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        {/* Quick action to view client */}
                        {matchingClient && (
                          <div className="mt-3 pt-3 border-t">
                            <Link 
                              to={`/clients/${matchingClient.id}`}
                              className="text-sm font-medium flex items-center justify-center py-2 rounded-lg transition-colors"
                              style={{ backgroundColor: `${COMPANY.primaryColor}15`, color: COMPANY.primaryColor }}
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              {language === 'es' ? 'Ver Ficha del Cliente' : 'View Client Card'}
                            </Link>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ADD LUMP SUM ITEM MODAL */}
        {showAddLumpSumModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg">
              <CardHeader className="bg-blue-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center">
                  <Plus className="mr-2 h-5 w-5" />
                  {language === 'es' ? 'Agregar Item a Biblioteca' : 'Add Item to Library'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label>{language === 'es' ? 'Categoría (Inglés)' : 'Category (English)'} *</Label>
                  <Input
                    value={newLumpSumItem.category}
                    onChange={(e) => setNewLumpSumItem({ ...newLumpSumItem, category: e.target.value })}
                    placeholder="e.g., Demolition Labor"
                  />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Categoría (Español)' : 'Category (Spanish)'}</Label>
                  <Input
                    value={newLumpSumItem.category_es}
                    onChange={(e) => setNewLumpSumItem({ ...newLumpSumItem, category_es: e.target.value })}
                    placeholder="e.g., Demolición"
                  />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Descripción (Inglés)' : 'Description (English)'} *</Label>
                  <Input
                    value={newLumpSumItem.description}
                    onChange={(e) => setNewLumpSumItem({ ...newLumpSumItem, description: e.target.value })}
                    placeholder="e.g., Tear out wet drywall"
                  />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Descripción (Español)' : 'Description (Spanish)'}</Label>
                  <Input
                    value={newLumpSumItem.description_es}
                    onChange={(e) => setNewLumpSumItem({ ...newLumpSumItem, description_es: e.target.value })}
                    placeholder="e.g., Remover drywall mojado"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setShowAddLumpSumModal(false);
                      setNewLumpSumItem({ category: '', category_es: '', description: '', description_es: '' });
                    }}
                  >
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button 
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={handleAddLumpSumItem}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {language === 'es' ? 'Agregar' : 'Add Item'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* RESOURCES TAB */}
        {activeTab === 'resources' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <BookOpen className="mr-2 h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                  {language === 'es' ? 'Centro de Recursos' : 'Resource Center'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {language === 'es' 
                    ? 'Manuales de la empresa, videos de capacitación y listas de verificación' 
                    : 'Company manuals, training videos, and project checklists'}
                </p>
              </div>
            </div>

            {/* Resource Sections Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              {/* Company Manual / Best Practices */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-t-lg py-4">
                  <CardTitle className="flex items-center text-lg">
                    <FileText className="mr-2 h-5 w-5" />
                    {language === 'es' ? 'Manual de la Empresa' : 'Company Manual'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-sm text-gray-600 mb-4">
                    {language === 'es' 
                      ? 'Mejores prácticas, procedimientos y políticas de la empresa.' 
                      : 'Best practices, procedures, and company policies.'}
                  </p>
                  <div className="space-y-2">
                    <Link to="/resources/manual" className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-blue-50 transition-colors">
                      <span className="text-sm font-medium">{language === 'es' ? 'Procedimientos del Sitio de Trabajo' : 'Job Site Procedures'}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </Link>
                    <Link to="/resources/manual" className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-blue-50 transition-colors">
                      <span className="text-sm font-medium">{language === 'es' ? 'Servicio al Cliente' : 'Customer Service'}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </Link>
                    <Link to="/resources/manual" className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-blue-50 transition-colors">
                      <span className="text-sm font-medium">{language === 'es' ? 'Protocolos de Seguridad' : 'Safety Protocols'}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </Link>
                    <Link to="/resources/manual" className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-blue-50 transition-colors">
                      <span className="text-sm font-medium">{language === 'es' ? 'Control de Calidad' : 'Quality Control'}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </Link>
                  </div>
                  <Link to="/resources/manual">
                    <Button className="w-full mt-4 text-white" style={{ backgroundColor: COMPANY.primaryColor }}>
                      <BookOpen className="mr-2 h-4 w-4" />
                      {language === 'es' ? 'Ver Manual Completo' : 'View Full Manual'}
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Training Videos */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-t-lg py-4">
                  <CardTitle className="flex items-center text-lg">
                    <Video className="mr-2 h-5 w-5" />
                    {language === 'es' ? 'Videos de Capacitación' : 'Training Videos'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-sm text-gray-600 mb-4">
                    {language === 'es' 
                      ? 'Tutoriales sobre cómo usar el software y capacitación de campo.' 
                      : 'Tutorials on how to use the software and field training.'}
                  </p>
                  <div className="space-y-2">
                    <Link to="/resources/videos" className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-purple-50 transition-colors">
                      <div className="flex items-center">
                        <PlayCircle className="h-4 w-4 text-purple-500 mr-2" />
                        <span className="text-sm font-medium">{language === 'es' ? 'Crear un Presupuesto' : 'Creating an Estimate'}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">5:30</Badge>
                    </Link>
                    <Link to="/resources/videos" className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-purple-50 transition-colors">
                      <div className="flex items-center">
                        <PlayCircle className="h-4 w-4 text-purple-500 mr-2" />
                        <span className="text-sm font-medium">{language === 'es' ? 'Usar el Generador AI' : 'Using AI Generator'}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">4:15</Badge>
                    </Link>
                    <Link to="/resources/videos" className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-purple-50 transition-colors">
                      <div className="flex items-center">
                        <PlayCircle className="h-4 w-4 text-purple-500 mr-2" />
                        <span className="text-sm font-medium">{language === 'es' ? 'Gestión de Clientes' : 'Managing Clients'}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">3:45</Badge>
                    </Link>
                    <Link to="/resources/videos" className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-purple-50 transition-colors">
                      <div className="flex items-center">
                        <PlayCircle className="h-4 w-4 text-purple-500 mr-2" />
                        <span className="text-sm font-medium">{language === 'es' ? 'Seguridad en el Trabajo' : 'Job Site Safety'}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">8:20</Badge>
                    </Link>
                  </div>
                  <Link to="/resources/videos">
                    <Button className="w-full mt-4 bg-purple-600 hover:bg-purple-700 text-white">
                      <Video className="mr-2 h-4 w-4" />
                      {language === 'es' ? 'Ver Todos los Videos' : 'View All Videos'}
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Project Checklists */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-t-lg py-4">
                  <CardTitle className="flex items-center text-lg">
                    <CheckSquare className="mr-2 h-5 w-5" />
                    {language === 'es' ? 'Listas de Verificación' : 'Project Checklists'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-sm text-gray-600 mb-4">
                    {language === 'es' 
                      ? 'Listas para reducir errores y asegurar calidad en cada proyecto.' 
                      : 'Checklists to reduce errors and ensure quality on every project.'}
                  </p>
                  <div className="space-y-2">
                    <Link to="/resources/checklists" className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-green-50 transition-colors">
                      <span className="text-sm font-medium">{language === 'es' ? 'Pre-Trabajo (Antes del Inicio)' : 'Pre-Job Walkthrough'}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </Link>
                    <Link to="/resources/checklists" className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-green-50 transition-colors">
                      <span className="text-sm font-medium">{language === 'es' ? 'Lista Diaria del Sitio' : 'Daily Site Checklist'}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </Link>
                    <Link to="/resources/checklists" className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-green-50 transition-colors">
                      <span className="text-sm font-medium">{language === 'es' ? 'Cierre del Proyecto' : 'Project Closeout'}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </Link>
                    <Link to="/resources/checklists" className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-green-50 transition-colors">
                      <span className="text-sm font-medium">{language === 'es' ? 'Inspección de Seguridad' : 'Safety Inspection'}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </Link>
                  </div>
                  <Link to="/resources/checklists">
                    <Button className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white">
                      <CheckSquare className="mr-2 h-4 w-4" />
                      {language === 'es' ? 'Ver Todas las Listas' : 'View All Checklists'}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
              
              {/* AI Training Card */}
              <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center text-purple-800">
                    <Brain className="mr-2 h-5 w-5 text-purple-600" />
                    {language === 'es' ? 'Entrenamiento de IA' : 'AI Training'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-purple-700 mb-4">
                    {language === 'es' 
                      ? 'Entrene al asistente de IA con patrones personalizados de Q&A para cumplimiento, satisfacción del cliente y mejores prácticas.' 
                      : 'Train the AI assistant with custom Q&A patterns for compliance, customer satisfaction, and best practices.'}
                  </p>
                  <div className="space-y-2">
                    <Link to="/ai-training" className="flex items-center justify-between p-2 bg-white/70 rounded hover:bg-white transition-colors">
                      <span className="text-sm">Florida Code Compliance</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </Link>
                    <Link to="/ai-training" className="flex items-center justify-between p-2 bg-white/70 rounded hover:bg-white transition-colors">
                      <span className="text-sm">Customer Satisfaction</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </Link>
                    <Link to="/ai-training" className="flex items-center justify-between p-2 bg-white/70 rounded hover:bg-white transition-colors">
                      <span className="text-sm">Loss Prevention</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </Link>
                  </div>
                  <Link to="/ai-training">
                    <Button className="w-full mt-4 bg-purple-600 hover:bg-purple-700 text-white" data-testid="ai-training-link">
                      <Brain className="mr-2 h-4 w-4" />
                      {language === 'es' ? 'Administrar Patrones de IA' : 'Manage AI Patterns'}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>

            {/* Quick Tips Section */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-100 rounded-full">
                    <FileQuestion className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800">
                      {language === 'es' ? '¿Nuevo en EstimatePro?' : 'New to EstimatePro?'}
                    </h3>
                    <p className="text-sm text-amber-700 mt-1">
                      {language === 'es' 
                        ? 'Comience con el video "Crear un Presupuesto" y luego explore las listas de verificación del proyecto para asegurar calidad en cada trabajo.' 
                        : 'Start with the "Creating an Estimate" video, then explore the project checklists to ensure quality on every job.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && (
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
                <CardTitle className="flex items-center"><Settings className="mr-2 h-5 w-5" />{language === 'es' ? 'Configuración de la Empresa' : 'Company Settings'}</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-gray-600 mb-6">{language === 'es' ? 'Administre la información de su empresa, logo, colores y términos.' : 'Manage your company information, logo, colors, and terms.'}</p>
                <Link to="/settings">
                  <Button style={{ backgroundColor: COMPANY.primaryColor }} className="text-white">
                    <Settings className="mr-2 h-4 w-4" />
                    {language === 'es' ? 'Abrir Configuración' : 'Open Settings'}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        )}

      </main>

      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md shadow-xl">
            <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
              <CardTitle className="flex items-center"><UserPlus className="mr-2 h-5 w-5" />{language === 'es' ? 'Nuevo Lead' : 'New Lead'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              {/* Quick Template Selection */}
              {templates.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <label className="text-purple-700 text-sm font-medium flex items-center mb-2">
                    <FileText className="mr-1 h-4 w-4" />
                    {language === 'es' ? 'Crear desde Plantilla (Opcional)' : 'Create from Template (Optional)'}
                  </label>
                  <select 
                    value={selectedTemplate?.id || ''}
                    onChange={(e) => {
                      const template = templates.find(t => t.id === e.target.value);
                      setSelectedTemplate(template || null);
                      if (template) {
                        setNewLead({...newLead, project_type: template.project_type});
                      }
                    }}
                    className="w-full border border-purple-300 rounded-md px-3 py-2 text-sm bg-white"
                  >
                    <option value="">{language === 'es' ? '-- Seleccionar Plantilla --' : '-- Select Template --'}</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name} ({t.line_items?.length || 0} items)</option>
                    ))}
                  </select>
                  {selectedTemplate && (
                    <p className="text-xs text-purple-600 mt-1">
                      {language === 'es' 
                        ? `Se aplicarán ${selectedTemplate.line_items?.length || 0} artículos y ${selectedTemplate.general_fees?.length || 0} tarifas`
                        : `Will apply ${selectedTemplate.line_items?.length || 0} items and ${selectedTemplate.general_fees?.length || 0} fees`}
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="text-gray-700 text-sm font-medium">{language === 'es' ? 'Nombre *' : 'Name *'}</label>
                <Input value={newLead.client_name} onChange={(e) => setNewLead({...newLead, client_name: e.target.value})} data-testid="new-lead-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-gray-700 text-sm font-medium">Email</label><Input type="email" value={newLead.client_email} onChange={(e) => setNewLead({...newLead, client_email: e.target.value})} /></div>
                <div><label className="text-gray-700 text-sm font-medium">{language === 'es' ? 'Teléfono' : 'Phone'}</label><Input value={newLead.client_phone} onChange={(e) => setNewLead({...newLead, client_phone: e.target.value})} /></div>
              </div>
              <div><label className="text-gray-700 text-sm font-medium">{language === 'es' ? 'Dirección' : 'Address'}</label><Input value={newLead.client_address} onChange={(e) => setNewLead({...newLead, client_address: e.target.value})} /></div>
              <div>
                <label className="text-gray-700 text-sm font-medium">{language === 'es' ? 'Tipo de Proyecto' : 'Project Type'}</label>
                <select value={newLead.project_type} onChange={(e) => setNewLead({...newLead, project_type: e.target.value})} className="w-full border rounded-md px-3 py-2">
                  {projectTypes.map(t => <option key={t.value} value={t.value}>{language === 'es' ? t.label_es : t.label}</option>)}
                </select>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => { setShowAddLeadModal(false); setSelectedTemplate(null); }}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
                <Button onClick={() => selectedTemplate ? handleAddLeadFromTemplate() : handleAddLead()} style={{ backgroundColor: COMPANY.primaryColor }} className="text-white" data-testid="create-lead-btn">
                  {selectedTemplate ? (language === 'es' ? 'Crear con Plantilla' : 'Create with Template') : (language === 'es' ? 'Crear' : 'Create')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Lead Duplicate Warning Modal */}
      {showLeadDuplicateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg shadow-xl">
            <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-t-lg">
              <CardTitle className="flex items-center">
                <AlertTriangle className="mr-2 h-5 w-5" />
                {language === 'es' ? 'Posible Duplicado Detectado' : 'Possible Duplicate Detected'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <p className="text-gray-600">
                {language === 'es' 
                  ? 'Se encontraron registros similares. ¿Desea crear un nuevo trabajo de todos modos?'
                  : 'Similar records were found. Do you want to create a new job anyway?'}
              </p>
              
              {/* Show existing duplicates */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                <p className="text-sm font-semibold text-amber-800 mb-2">
                  {language === 'es' ? 'Registros existentes:' : 'Existing records:'}
                </p>
                {leadDuplicates.map((dup, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-amber-100 last:border-0">
                    <div>
                      <p className="font-medium text-gray-800">{dup.name}</p>
                      <p className="text-xs text-gray-500">
                        {dup.type === 'lead' ? '📋 Lead' : '👤 Client'} • {dup.matchReason} match
                        {dup.project_type && ` • ${dup.project_type}`}
                      </p>
                    </div>
                    <Badge variant="outline" className={dup.type === 'lead' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}>
                      {dup.status || dup.type}
                    </Badge>
                  </div>
                ))}
              </div>
              
              {/* Job suffix input */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <label className="text-sm font-medium text-blue-800 block mb-2">
                  {language === 'es' ? 'Agregar descripción del trabajo (opcional):' : 'Add job description (optional):'}
                </label>
                <Input 
                  value={jobSuffix}
                  onChange={(e) => setJobSuffix(e.target.value)}
                  placeholder={language === 'es' ? 'ej: Cocina, Techo, Baño, Job 2' : 'e.g., Kitchen, Roof, Bathroom, Job 2'}
                  className="bg-white"
                />
                <p className="text-xs text-blue-600 mt-1">
                  {language === 'es' 
                    ? `El nombre será: "${pendingLeadData?.client_name}${jobSuffix ? ` - ${jobSuffix}` : ''}"` 
                    : `Name will be: "${pendingLeadData?.client_name}${jobSuffix ? ` - ${jobSuffix}` : ''}"`}
                </p>
              </div>
              
              <div className="flex justify-end space-x-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowLeadDuplicateModal(false);
                    setPendingLeadData(null);
                    setJobSuffix('');
                    setLeadDuplicates([]);
                  }}
                >
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button 
                  onClick={() => handleForceCreateLead(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Plus className="mr-1 h-4 w-4" />
                  {language === 'es' ? 'Crear Nuevo Trabajo' : 'Create as New Job'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* AI Office Assistant */}
      <AIAssistant />
      
      {/* Storage Dashboard Modal (Hidden - accessible via icon) */}
      {showStorageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg shadow-xl">
            <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-t-lg">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center">
                  <HardDrive className="mr-2 h-5 w-5" />
                  Storage Dashboard
                </span>
                <Button variant="ghost" size="sm" onClick={() => setShowStorageModal(false)} className="text-white hover:bg-white/20">
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {storageLoading ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                  <p className="text-gray-500 mt-2">Loading storage stats...</p>
                </div>
              ) : storageStats ? (
                <div className="space-y-6">
                  {/* Storage Overview */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-blue-600">{storageStats.file_count}</div>
                      <div className="text-sm text-blue-700">Total Files</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {storageStats.total_size_mb < 1 
                          ? `${Math.round(storageStats.total_size_bytes / 1024)} KB`
                          : storageStats.total_size_mb < 1000 
                            ? `${storageStats.total_size_mb.toFixed(1)} MB`
                            : `${storageStats.total_size_gb.toFixed(2)} GB`
                        }
                      </div>
                      <div className="text-sm text-green-700">Total Size</div>
                    </div>
                  </div>
                  
                  {/* Storage Details */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
                      <Database className="h-4 w-4 mr-2" />
                      MongoDB GridFS Storage
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Storage Type:</span>
                        <span className="font-medium">GridFS (Persistent)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Bytes:</span>
                        <span className="font-mono text-xs">{storageStats.total_size_bytes.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Avg File Size:</span>
                        <span className="font-medium">
                          {storageStats.file_count > 0 
                            ? `${Math.round(storageStats.total_size_bytes / storageStats.file_count / 1024)} KB`
                            : '0 KB'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Cost Estimate */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-800 mb-2 flex items-center">
                      <DollarSign className="h-4 w-4 mr-2" />
                      Estimated Cost
                    </h4>
                    <p className="text-sm text-amber-700">
                      {storageStats.total_size_mb < 512 
                        ? "Within MongoDB free tier (512 MB)"
                        : storageStats.total_size_mb < 2000
                          ? `~$9/month (Shared M2: 2GB tier)`
                          : `~$${Math.ceil(storageStats.total_size_gb * 5)}/month (estimated)`
                      }
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      Files persist in MongoDB - no extra services needed
                    </p>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex justify-between items-center pt-2 border-t">
                    <Button variant="outline" size="sm" onClick={fetchStorageStats}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                    <p className="text-xs text-gray-400">
                      Last updated: {new Date().toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <HardDrive className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                  <p>Unable to load storage stats</p>
                  <Button variant="outline" size="sm" onClick={fetchStorageStats} className="mt-4">
                    Try Again
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Import Duplicates Modal */}
      {showDuplicatesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[80vh] shadow-xl overflow-hidden flex flex-col">
            <CardHeader className="bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center">
                  <AlertTriangle className="mr-2 h-5 w-5" />
                  {language === 'es' ? 'Duplicados Encontrados' : 'Duplicates Found'} ({importDuplicates.length})
                </span>
                <Button variant="ghost" size="sm" onClick={() => setShowDuplicatesModal(false)} className="text-white hover:bg-white/20">
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
              <p className="text-amber-100 text-sm mt-1">
                {language === 'es' 
                  ? 'Estos clientes ya existen. Puedes añadirlos manualmente si deseas crear duplicados.'
                  : 'These clients already exist. You can add them manually if you want to create duplicates.'}
              </p>
            </CardHeader>
            <CardContent className="pt-4 overflow-y-auto flex-1">
              <div className="space-y-3">
                {importDuplicates.map((dup, index) => (
                  <div key={index} className="border rounded-lg p-3 bg-gray-50 hover:bg-gray-100">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-800">{dup.name}</span>
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                            {language === 'es' ? 'Fila' : 'Row'} {dup.row}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 mt-1 space-y-0.5">
                          {dup.email && <div><Mail className="h-3 w-3 inline mr-1" />{dup.email}</div>}
                          {dup.phone && <div><Phone className="h-3 w-3 inline mr-1" />{dup.phone}</div>}
                          {dup.address && <div><MapPin className="h-3 w-3 inline mr-1" />{dup.address}{dup.city ? `, ${dup.city}` : ''}{dup.state ? `, ${dup.state}` : ''}</div>}
                        </div>
                        <div className="text-xs text-red-600 mt-2 bg-red-50 px-2 py-1 rounded inline-block">
                          {dup.reason}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/clients/${dup.existing_id}`)}
                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {language === 'es' ? 'Ver Existente' : 'View Existing'}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleAddDuplicateClient(dup)}
                          style={{ backgroundColor: COMPANY.primaryColor }}
                          className="text-white"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {language === 'es' ? 'Añadir Igual' : 'Add Anyway'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {importDuplicates.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                  <p>{language === 'es' ? '¡Todos los duplicados procesados!' : 'All duplicates processed!'}</p>
                </div>
              )}
            </CardContent>
            <div className="p-4 border-t bg-gray-50 flex justify-between">
              <Button variant="outline" onClick={() => setShowDuplicatesModal(false)}>
                {language === 'es' ? 'Cerrar' : 'Close'}
              </Button>
              <Button
                variant="outline"
                className="text-green-600 border-green-300 hover:bg-green-50"
                onClick={async () => {
                  for (const dup of importDuplicates) {
                    await handleAddDuplicateClient(dup);
                  }
                  setShowDuplicatesModal(false);
                }}
                disabled={importDuplicates.length === 0}
              >
                <Plus className="h-4 w-4 mr-1" />
                {language === 'es' ? 'Añadir Todos' : 'Add All'} ({importDuplicates.length})
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Add Tag Modal - Single Client */}
      {showTagModal && tagModalClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md shadow-xl">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center">
                  <Tag className="mr-2 h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                  {language === 'es' ? 'Añadir Etiqueta' : 'Add Tag'}
                </span>
                <Button variant="ghost" size="sm" onClick={() => { setShowTagModal(false); setTagModalClient(null); }}>
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
              <p className="text-sm text-gray-500">{tagModalClient.name}</p>
            </CardHeader>
            <CardContent className="pt-4">
              <p className="text-sm text-gray-600 mb-3">{language === 'es' ? 'Selecciona una etiqueta:' : 'Select a tag:'}</p>
              <div className="flex flex-wrap gap-2">
                {predefinedTags.map(tag => {
                  const isActive = tagModalClient.tags && tagModalClient.tags.includes(tag.id);
                  return (
                    <Badge
                      key={tag.id}
                      className={`cursor-pointer px-3 py-1.5 text-sm ${isActive ? 'ring-2 ring-offset-2' : 'hover:opacity-80'}`}
                      style={{ backgroundColor: tag.color, color: 'white' }}
                      onClick={() => {
                        if (isActive) {
                          handleRemoveTagFromClient(tagModalClient.id, tag.id);
                        } else {
                          handleAddTagToClient(tagModalClient.id, tag.id);
                        }
                        // Update local state
                        setTagModalClient(prev => ({
                          ...prev,
                          tags: isActive 
                            ? (prev.tags || []).filter(t => t !== tag.id)
                            : [...(prev.tags || []), tag.id]
                        }));
                      }}
                    >
                      {tag.label} {isActive && '✓'}
                    </Badge>
                  );
                })}
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-gray-500 mb-2">{language === 'es' ? 'Etiquetas actuales:' : 'Current tags:'}</p>
                <div className="flex flex-wrap gap-1">
                  {tagModalClient.tags && tagModalClient.tags.length > 0 ? (
                    tagModalClient.tags.map(tag => (
                      <Badge key={tag} style={{ backgroundColor: getTagColor(tag), color: 'white' }} className="text-xs">
                        {getTagLabel(tag)}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-gray-400">{language === 'es' ? 'Sin etiquetas' : 'No tags'}</span>
                  )}
                </div>
              </div>
            </CardContent>
            <div className="p-4 border-t flex justify-end">
              <Button onClick={() => { setShowTagModal(false); setTagModalClient(null); }}>
                {language === 'es' ? 'Cerrar' : 'Close'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Bulk Tag Modal - Multiple Clients */}
      {showBulkTagModal && selectedClients.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md shadow-xl">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center">
                  <Tags className="mr-2 h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                  {language === 'es' ? 'Etiquetar Clientes' : 'Tag Clients'}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setShowBulkTagModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
              <p className="text-sm text-gray-500">
                {language === 'es' 
                  ? `Añadir etiquetas a ${selectedClients.length} cliente(s)`
                  : `Add tags to ${selectedClients.length} client(s)`}
              </p>
            </CardHeader>
            <CardContent className="pt-4">
              <p className="text-sm text-gray-600 mb-3">{language === 'es' ? 'Selecciona etiquetas:' : 'Select tags:'}</p>
              <div className="flex flex-wrap gap-2">
                {predefinedTags.map(tag => (
                  <Badge
                    key={tag.id}
                    className="cursor-pointer px-3 py-1.5 text-sm hover:opacity-80"
                    style={{ backgroundColor: tag.color, color: 'white' }}
                    onClick={() => handleBulkAddTags([tag.id])}
                  >
                    {tag.label}
                  </Badge>
                ))}
              </div>
            </CardContent>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowBulkTagModal(false)}>
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Bulk Status Change Modal - Multiple Clients */}
      {showBulkStatusModal && selectedClients.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md shadow-xl">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center">
                  <RefreshCw className="mr-2 h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                  {language === 'es' ? 'Cambiar Estado' : 'Change Status'}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setShowBulkStatusModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
              <p className="text-sm text-gray-500">
                {language === 'es' 
                  ? `Actualizar estado de ${selectedClients.length} cliente(s)`
                  : `Update status for ${selectedClients.length} client(s)`}
              </p>
            </CardHeader>
            <CardContent className="pt-4">
              <p className="text-sm text-gray-600 mb-3">{language === 'es' ? 'Selecciona nuevo estado:' : 'Select new status:'}</p>
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant="outline" 
                  className="justify-start text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => handleBulkStatusChange('lead')}
                >
                  <Target className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Lead' : 'Lead'}
                </Button>
                <Button 
                  variant="outline" 
                  className="justify-start text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={() => handleBulkStatusChange('prospect')}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Prospecto' : 'Prospect'}
                </Button>
                <Button 
                  variant="outline" 
                  className="justify-start text-green-600 border-green-200 hover:bg-green-50"
                  onClick={() => handleBulkStatusChange('active')}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Activo' : 'Active'}
                </Button>
                <Button 
                  variant="outline" 
                  className="justify-start text-purple-600 border-purple-200 hover:bg-purple-50"
                  onClick={() => handleBulkStatusChange('completed')}
                >
                  <Award className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Completado' : 'Completed'}
                </Button>
                <Button 
                  variant="outline" 
                  className="justify-start text-gray-600 border-gray-200 hover:bg-gray-50"
                  onClick={() => handleBulkStatusChange('archived')}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Archivado' : 'Archived'}
                </Button>
                <Button 
                  variant="outline" 
                  className="justify-start text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleBulkStatusChange('lost')}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Perdido' : 'Lost'}
                </Button>
              </div>
            </CardContent>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowBulkStatusModal(false)}>
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
